import React, { useCallback, useEffect, useRef, useState } from "react";
import { C } from "../../../constants/design";
import { BOX, INVENTORY_IMGS } from "../../../constants/items";
import type { Player } from "../../../types/game";
import type { EquipmentStats } from "../../league-duel/engine/combat";
import type { AvatarLayers } from "../../league-duel/types/leagueDuel";
import { LayeredCharacter } from "../../league-duel/components/LayeredCharacter";
import {
  PveBonus,
  BONUS_STAGES,
  BossResult,
  CombatActor,
  CombatSequence,
  ConditionState,
  MAX_ACTIVE_BONUSES,
  MAX_CONDITION,
  applyBonusChoice,
  calculatePveCondition,
  CONDITION_REGEN_MS,
  formatConditionTime,
  getStageRewards,
  isBonusStage,
  maxConditionWithBonuses,
  maxHpFromStats,
  simulateCombat,
  getEnemyStatPreview,
  defenseReductionFor,
} from "../engine/pveEngine";
import type { EnemyStatPreview } from "../engine/pveEngine";
import { getPveProgress, startOrResumePveRun, savePveStageWin, savePveBonusChoice, resetPveRun, insertPveRun, completePveTier, savePveSpellLoadout, savePveVariant } from "../engine/pveService";
import type { PveProgress, PveStageWinResult } from "../engine/pveService";
import { PVE_TIERS, getTier, getEncounter, actForTier } from "../content/pveTiers";
import { getEnemy, pickEnemyArt, pickFamiliarArt } from "../content/pveEnemies";
import { getBoost, BOOST_POOL, BOOST_DRAFT_SIZE, isValidBoost } from "../content/pveBoosts";
import type { PveBoostId } from "../content/pveBoosts";
import { getSpell, isValidSpell, spellsUnlockedAt, spellComputedValues, SPELL_POOL, SPELL_LOADOUT_SIZE, MANDATORY_SPELLS, PVE_SPELLS } from "../content/pveSpells";
import type { PveSpellId } from "../content/pveSpells";
import { getBossEffect } from "../content/pveBossEffects";
import { PVE_PETS_ENABLED, PET_SPELL_POOL, PET_ENERGY_PER_TURN, PET_STRIKE_DAMAGE_PERCENT, PET_HEAL_PERCENT_MASTER_DEF, getPetSpell } from "../content/pvePets";
import type { PetSpellId } from "../content/pvePets";
import { resolvePveVariant, PVE_AB_HASH_ROLLOUT } from "../engine/pveVariant";
import type { PveVariant } from "../engine/pveVariant";
import {
  ENERGY_MAX,
  ENERGY_PER_TURN,
  PVE_ROTATION_ENABLED,
  ROTATION_GAUGE_SCALE,
  ROTATION_ENERGY_MAX,
  ROTATION_SPELL_DAMAGE_MULT,
  rotationSpellCost,
  createInteractiveCombat,
  castSpell,
  castPetSpell,
  endTurn,
  canCastSpell,
  canCastPetSpell,
  shouldAutoEndTurn,
  rotationPlayerAction,
  rotationHead,
  enemyRageMultiplier,
  finalizeInteractiveCombat,
  interactiveVariantStats,
} from "../engine/pveCombatInteractive";
import type { InteractiveCombatState } from "../engine/pveCombatInteractive";
import { logPveFight, logPveBoostDraft } from "../engine/pveTelemetry";
import { useST } from "../i18n";

const TOTAL_STAGES = 10;

// In-memory session cache (per profile_id) of the SOURCE progression — never a
// frozen displayed condition. On re-entry we recompute the live condition from
// pve_condition + pve_condition_updated_at, exactly like a fresh Supabase fetch.
const pveSessionCache = new Map<string, { progress?: PveProgress }>();

function condFromProgress(p: PveProgress): ConditionState {
  return p.pve_condition_updated_at
    ? calculatePveCondition(p.pve_condition, p.pve_condition_updated_at)
    : { condition: p.pve_condition, msToFull: 0, isFull: p.pve_condition >= MAX_CONDITION };
}

// Official match_gold coin asset (same as the top bar / wallet across the app).
const COIN_ICON = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/CoinIcon.png";
const GoldIcon = ({ big }: { big?: boolean }) => (
  <img src={COIN_ICON} alt="" style={{ width: big ? 28 : 22, height: big ? 28 : 22, objectFit: "contain", verticalAlign: "middle" }} />
);

// ── Restyle "recent-game" look — fonts + HUD tokens (presentation only) ──────
export const FONT = {
  display: "'Chakra Petch', system-ui, sans-serif", // numbers / titles / CTA / HUD
  ui: "'Barlow', system-ui, sans-serif",            // body, descriptions
  label: "'Barlow Semi Condensed', system-ui, sans-serif", // tracked micro-labels
};
// Cut-corner "bevel" HUD panel.
export const ST_BEVEL = "polygon(0 10px,10px 0,100% 0,100% calc(100% - 10px),calc(100% - 10px) 100%,0 100%)";
// Font import + shared keyframes for the restyle (idempotent across screens).
export const ST_STYLE = `
@import url('https://fonts.googleapis.com/css2?family=Chakra+Petch:wght@500;600;700&family=Barlow:wght@400;500;600;700;800;900&family=Barlow+Semi+Condensed:wght@600;700;800&display=swap');
@keyframes stShine{0%{transform:translateX(-140%) skewX(-22deg)}55%,100%{transform:translateX(260%) skewX(-22deg)}}
@keyframes stCtaGlow{0%,100%{box-shadow:0 10px 26px rgba(255,72,44,.45)}50%{box-shadow:0 14px 46px rgba(255,72,44,.8)}}
@keyframes stGlowPulse{0%,100%{opacity:.5}50%{opacity:1}}
@keyframes stLootPop{from{opacity:0;transform:scale(.7) translateY(8px)}to{opacity:1;transform:scale(1) translateY(0)}}
@keyframes stNode{0%,100%{box-shadow:0 0 0 0 rgba(245,179,1,.55)}50%{box-shadow:0 0 0 7px rgba(245,179,1,0)}}
@keyframes stBanner{0%{opacity:0;transform:translateY(14px) scale(.94)}100%{opacity:1;transform:translateY(0) scale(1)}}
`;

/** Condition bar fill gradient by threshold (≥66 green · 33–65 gold · <33 red). */
function conditionFill(v: number): string {
  if (v > 66) return "linear-gradient(180deg, #45E8A0, #16A85C)";
  if (v > 33) return "linear-gradient(180deg, #FFD24A, #F5A201)";
  return "linear-gradient(180deg, #FF6B6B, #FF3B47)";
}

// Drop legacy/unknown boost ids (e.g. old "physique") from a saved run so slots
// free up and combat ignores them cleanly. Self-heals on the next persist.
const cleanBoosts = (ids: string[]): PveBoostId[] => ids.filter(isValidBoost);

// Same self-heal for saved spell loadouts (A/B variant B). Also trims legacy
// 5-spell loadouts (saved before the mandatory Classic Strike was removed)
// down to the current SPELL_LOADOUT_SIZE.
const cleanSpells = (ids: string[]): PveSpellId[] => ids.filter(isValidSpell).slice(0, SPELL_LOADOUT_SIZE);

// Random sample of n items (Fisher–Yates, non-mutating). Used for the boost draft.
function sampleN<T>(arr: T[], n: number): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a.slice(0, n);
}

export interface PveCompletePatch {
  match_gold_delta: number;
  /** Absolute duel_xp persisted in DB (like the Duel's xpAfter), or null if unchanged. */
  duel_xp_after: number | null;
  pve_condition: number;
  pve_best_stage: number;
  pve_trial_chests_delta: number;
}

export interface StadiumTrialScreenProps {
  player: Player & { profile_id?: string };
  playerStats: EquipmentStats;
  /** Player game level (playerLevel(earned + duel_xp)) — telemetry relevance filter. */
  playerLevel: number;
  /** Extra energy/turn from equipped items (ball affix). 0 = default 6/turn. */
  energyBonus?: number;
  onExit: () => void;
  onComplete: (patch: PveCompletePatch) => void;
  onChestEarned: () => void;
  /** Player avatar layers, forwarded from LeagueWidget for the future combat render. */
  homeLayers?: AvatarLayers;
}

type Phase =
  | { kind: "pre-run" }
  | { kind: "spell-select" }  // variant B: pick the run's spells before stage 1
  | { kind: "fighting"; stage: number; sequence: CombatSequence }
  | { kind: "fighting-live"; stage: number; combat: InteractiveCombatState } // variant B
  | { kind: "boss-result"; stage: number; result: BossResult }
  | { kind: "camp"; clearedStage: number; resumed?: boolean }
  | { kind: "run-result"; reachedStage: number; cleared: boolean; totalXp: number; totalGold: number; chestEarned: boolean; error?: string; retryable?: boolean; saving?: boolean; saved?: boolean };

interface RunState {
  stage: number;
  tier: number;
  activeBonuses: PveBonus[];
  spellLoadout: PveSpellId[]; // variant B — [] means "not picked yet"
  condition: number;
  totalXp: number;
  totalGold: number;
  chestEarned: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────

export function StadiumTrialScreen({ player, playerStats, playerLevel, energyBonus = 0, onExit, onComplete, onChestEarned, homeLayers }: StadiumTrialScreenProps) {

  const [phase, setPhase] = useState<Phase>({ kind: "pre-run" });
  const [runState, setRunState] = useState<RunState | null>(null);
  // Fresh PVE progression from Supabase (source of truth for Start/Resume).
  const [pveProgress, setPveProgress] = useState<PveProgress | null>(null);
  // Pre-run field values — null while their Supabase fetch is still in flight,
  // so the UI shows "…" instead of a false fallback (0/0, 100/100).
  const [conditionState, setConditionState] = useState<ConditionState | null>(null);
  // Progress fetch FAILED (network) — distinct from "still loading": the Start
  // CTA stays blocked while loading (the A/B variant is unknown until the row
  // arrives), but unblocks on failure so an offline hiccup can't lock the mode.
  const [progressFailed, setProgressFailed] = useState(false);
  // Per-stage save state — credit happens on each win, not at run end.
  const [stageSaving, setStageSaving] = useState(false);
  const [stageError, setStageError] = useState<string | null>(null);
  const savingStageRef = useRef(false); // blocks double-click while a save is in flight
  const startPromiseRef = useRef<Promise<PveProgress | null> | null>(null); // background start/resume
  const finalizingRef = useRef(false); // blocks a concurrent finalize (defeat/clear)
  const retryFinalizeRef = useRef<(() => void) | null>(null); // re-runs the last failed finalize
  // Stage-win persistence starts at combat end (boss-result shown), NOT on the
  // Continue click — a refresh on the result screen must never re-roll the fight.
  const stageSavedKeyRef = useRef<string | null>(null); // "tier:stage" already persisted
  const stageResRef = useRef<PveStageWinResult | null>(null); // save result for that key
  const autoResumedRef = useRef(false); // guards the one-shot resume-to-camp on load
  const startedRef = useRef(false);      // set once the player manually starts a run

  // ── A/B bucket ── pure & synchronous (usable before any fetch resolves); the
  // persisted players.pve_variant (once loaded) always wins over the hash.
  const variant: PveVariant = resolvePveVariant(pveProgress?.pve_variant, player.profile_id);
  const spellsVariant = variant === "spells";

  // Persist the hash assignment ONCE (rollout ON only) so SQL joins stay trivial.
  // Fire-and-forget: a failed write never blocks play (variant is recomputed).
  const variantSavedRef = useRef(false);
  useEffect(() => {
    if (!PVE_AB_HASH_ROLLOUT || variantSavedRef.current) return;
    if (!pveProgress || pveProgress.pve_variant || !player.profile_id) return;
    variantSavedRef.current = true;
    void savePveVariant(player.profile_id, variant);
    setPveProgress((prev) => (prev ? { ...prev, pve_variant: variant } : prev));
  }, [pveProgress, player.profile_id, variant]);

  // While a run is active and sitting BETWEEN two stages, re-entering the trial
  // must land back on the Camp (the recovery phase), not the pre-run screen — so
  // the player can leave (e.g. play a Duel), let the condition regen, and return
  // to continue. Rebuilds the run from the persisted progress. One-shot: never
  // yanks the player back to camp after they've manually started or navigated.
  const maybeResumeCamp = useCallback((progress: PveProgress) => {
    if (autoResumedRef.current || startedRef.current) return;
    const cleared = progress.pve_last_rewarded_stage;
    if (progress.pve_run_status !== "active") return;
    if (cleared < 1 || cleared >= TOTAL_STAGES) return;       // stage-1 start / stage-10 finalize handled elsewhere
    if (progress.pve_current_stage !== cleared + 1) return;   // only a clean between-stages state
    autoResumedRef.current = true;
    const activeBonuses = cleanBoosts(progress.pve_active_bonuses);
    const ceiling = maxConditionWithBonuses(activeBonuses);
    const condition = progress.pve_condition_updated_at
      ? calculatePveCondition(progress.pve_condition, progress.pve_condition_updated_at, Date.now(), ceiling).condition
      : progress.pve_condition;
    setRunState({
      stage: cleared + 1,
      tier: progress.pve_current_tier ?? 1,
      activeBonuses,
      spellLoadout: cleanSpells(progress.pve_spell_loadout),
      condition,
      totalXp: 0, totalGold: 0, chestEarned: false, // per-run totals aren't persisted (run-result summary only)
    });
    setPhase({ kind: "camp", clearedStage: cleared, resumed: true });
  }, []);

  // Load pre-run data. Seed instantly from the session cache (recomputing the
  // live condition from the cached SOURCE values), then revalidate in background.
  useEffect(() => {
    const profileId = player.profile_id;
    if (!profileId) {
      const c = player.pve_condition != null && player.pve_condition_updated_at
        ? calculatePveCondition(player.pve_condition, player.pve_condition_updated_at)
        : { condition: MAX_CONDITION, msToFull: 0, isFull: true };
      setConditionState(c);
      return;
    }

    // 1. Instant seed from cache — real estimated values, no flash, no "...".
    const cached = pveSessionCache.get(profileId);
    if (cached?.progress) {
      setPveProgress(cached.progress);
      setConditionState(condFromProgress(cached.progress)); // recomputed, not frozen
      maybeResumeCamp(cached.progress);
    }

    // 2. Background revalidation (Supabase = source of truth; corrects if needed).
    getPveProgress(profileId).then((progress) => {
      if (progress) {
        setPveProgress(progress);
        setConditionState(condFromProgress(progress));
        maybeResumeCamp(progress);
      } else {
        setProgressFailed(true); // unblocks Start (control/hash fallback) despite the failure
        if (!cached?.progress) {
          // No cache and fetch failed → avoid a stuck "..." on condition.
          const c = player.pve_condition != null && player.pve_condition_updated_at
            ? calculatePveCondition(player.pve_condition, player.pve_condition_updated_at)
            : { condition: MAX_CONDITION, msToFull: 0, isFull: true };
          setConditionState(c);
        }
      }
    });
  }, []); // eslint-disable-line

  // Live regen tick — recompute the DISPLAYED condition from the SOURCE every
  // second while on the pre-run screen. calculatePveCondition is time-pure (no
  // network): the bar fills without needing a back-and-forth or a page refresh.
  useEffect(() => {
    if (phase.kind !== "pre-run") return;
    if (conditionState?.isFull) return;
    const id = setInterval(() => {
      if (pveProgress?.pve_condition_updated_at) {
        setConditionState(condFromProgress(pveProgress));
      } else if (player.pve_condition != null && player.pve_condition_updated_at) {
        setConditionState(calculatePveCondition(player.pve_condition, player.pve_condition_updated_at));
      }
    }, 1000);
    return () => clearInterval(id);
  }, [phase.kind, conditionState?.isFull, pveProgress, player]);

  // Mid-run regen tick — Camp is a voluntary recovery phase. Condition regens over
  // real time from the last persisted value (pve_condition + pve_condition_updated_at,
  // written on the previous stage win), so it keeps regening even while the player is
  // away (e.g. off doing a Duel). runState.condition is the SINGLE value fed to the
  // next fight → the bar can never diverge from what combat actually uses.
  useEffect(() => {
    if (phase.kind !== "camp") return;
    const base = pveProgress?.pve_condition;
    const updatedAt = pveProgress?.pve_condition_updated_at;
    if (base == null || !updatedAt) return;
    const ceiling = maxConditionWithBonuses(runState?.activeBonuses ?? []);
    const tick = () => {
      const live = calculatePveCondition(base, updatedAt, Date.now(), ceiling).condition;
      setRunState((prev) => (prev ? { ...prev, condition: live } : prev));
    };
    tick(); // apply immediately, no 1s wait
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [phase.kind, pveProgress?.pve_condition, pveProgress?.pve_condition_updated_at, runState?.activeBonuses]); // eslint-disable-line

  // Keep the session cache in sync with the freshest SOURCE progression + count.
  useEffect(() => {
    const profileId = player.profile_id;
    if (!profileId) return;
    const prev = pveSessionCache.get(profileId) ?? {};
    pveSessionCache.set(profileId, {
      ...prev,
      ...(pveProgress ? { progress: pveProgress } : {}),
    });
  }, [pveProgress, player.profile_id]);

  // ── Start OR resume a run — INSTANT launch, DB start/resume in background ──
  const startRun = useCallback(() => {
    if (phase.kind !== "pre-run") return;
    startedRef.current = true; // block any late resume-to-camp from overriding this

    const profileId = player.profile_id;
    const prog = pveProgress; // already fetched by the load effect

    // A/B race guard: launching before the players row (→ pve_variant) is known
    // would resolve to 'control' and fight stage 1 with the WRONG engine for a
    // spells-bucket player. The CTA is disabled while loading — this is the
    // belt-and-braces for a click that slips through.
    if (profileId && !prog && !progressFailed) {
      startedRef.current = false;
      return;
    }

    // Init synchronously from the loaded progression (or the player prop).
    let stage = 1;
    let activeBonuses: PveBonus[] = [];
    let spellLoadout: PveSpellId[] = [];
    let condition = (player.pve_condition != null && player.pve_condition_updated_at
      ? calculatePveCondition(player.pve_condition, player.pve_condition_updated_at)
      : { condition: MAX_CONDITION, msToFull: 0, isFull: true }).condition;
    const tier = prog?.pve_current_tier ?? player.pve_current_tier ?? 1;

    if (prog && prog.pve_run_status === "active") {
      stage = prog.pve_current_stage;
      activeBonuses = cleanBoosts(prog.pve_active_bonuses);
      spellLoadout = cleanSpells(prog.pve_spell_loadout);
    }
    if (prog) {
      condition = prog.pve_condition_updated_at
        ? calculatePveCondition(prog.pve_condition, prog.pve_condition_updated_at).condition
        : prog.pve_condition;
    }

    const initialRun: RunState = { stage, tier, activeBonuses, spellLoadout, condition, totalXp: 0, totalGold: 0, chestEarned: false };

    savingStageRef.current = false;
    stageSavedKeyRef.current = null;
    stageResRef.current = null;
    setStageSaving(false);
    setStageError(null);
    setRunState(initialRun);
    if (spellsVariant) {
      // Variant B: the loadout must exist before the first fight. A fresh run
      // (or a resume that quit before picking) goes through spell-select; a
      // resume with a saved loadout launches straight into the live fight.
      if (spellLoadout.length > 0) beginLiveFight(stage, initialRun);
      else setPhase({ kind: "spell-select" });
    } else {
      beginFight(stage, initialRun); // ← INSTANT, no await
    }

    // Ensure the run is 'active' in Supabase (and get a run_id) in the background.
    // Combat animation lasts several seconds — resolves well before the stage save.
    if (profileId) {
      startPromiseRef.current = (async () => {
        const p = await startOrResumePveRun(profileId);
        if (p) setPveProgress(p);
        return p;
      })();
    }
  }, [phase, player, pveProgress, progressFailed]); // eslint-disable-line

  // Run the per-stat fight ONCE (the simulation is the source of truth) and stash
  // the sequence in the phase so the animated CombatScreen only replays frames.
  const beginFight = (stage: number, run: RunState) => {
    const sequence = simulateCombat(stage, playerStats, run.activeBonuses, run.condition, run.tier);
    setPhase({ kind: "fighting", stage, sequence });
  };

  // Variant B: open a LIVE interactive fight (state machine driven by the player;
  // same inputs as beginFight, nothing persisted until the fight resolves).
  const beginLiveFight = (stage: number, run: RunState) => {
    // Rotation test: pets OFF (one variable at a time — the rotation replaces
    // the whole action system, mixing both would muddy the telemetry).
    const combat = createInteractiveCombat(
      stage, playerStats, run.spellLoadout, run.condition, run.tier, playerLevel, energyBonus,
      PVE_PETS_ENABLED && !PVE_ROTATION_ENABLED,
      PVE_ROTATION_ENABLED
    );
    setPhase({ kind: "fighting-live", stage, combat });
  };

  // Variant B — spell-select confirmed: lock the loadout for the whole run,
  // persist it in background (survives quit/refresh), launch stage 1.
  const handleSpellsConfirmed = useCallback((loadout: PveSpellId[]) => {
    if (!runState || phase.kind !== "spell-select") return;
    const nextRun: RunState = { ...runState, spellLoadout: loadout };
    setRunState(nextRun);
    const profileId = player.profile_id;
    if (profileId) {
      void savePveSpellLoadout(profileId, loadout);
      setPveProgress((prev) => (prev ? { ...prev, pve_spell_loadout: loadout } : prev));
    }
    beginLiveFight(nextRun.stage, nextRun);
  }, [runState, phase, player]); // eslint-disable-line

  // ── Persist a stage WIN — kicked as soon as the result screen appears ──────
  // Credits DB + local rewards once (guarded by savePveStageWin + local key).
  // Safe to call again (Continue / Retry): a persisted key is a no-op.
  const persistStageWin = useCallback(async (stage: number, result: BossResult, run: RunState) => {
    const key = `${run.tier}:${stage}`;
    if (stageSavedKeyRef.current === key) return; // already persisted
    if (savingStageRef.current) return;           // save in flight
    savingStageRef.current = true;
    setStageSaving(true);
    setStageError(null);

    // The background start/resume may still be resolving — wait for the run_id.
    const profileId = player.profile_id;
    let runId = pveProgress?.pve_run_id ?? null;
    if (!runId && startPromiseRef.current) {
      const p = await startPromiseRef.current;
      runId = p?.pve_run_id ?? null;
    }
    if (!profileId || !runId) {
      savingStageRef.current = false;
      setStageSaving(false);
      setStageError("Account/run error — please reload the trial.");
      return;
    }

    const res = await savePveStageWin({
      profileId,
      runId,
      stage,
      xpDelta: result.xpEarned,
      goldDelta: result.goldEarned,
      chestDelta: result.chestEarned ? 1 : 0,
      conditionAfter: result.conditionAfter,
      activeBonuses: run.activeBonuses,
    });

    savingStageRef.current = false;
    setStageSaving(false);

    if (res.status === "error") {
      // Nothing persisted → error + Retry on the result screen.
      setStageError("Stage not saved — check your connection and tap Retry.");
      return;
    }

    stageSavedKeyRef.current = key;
    stageResRef.current = res;

    // credited → apply local rewards ONCE (XP appears right on the result
    // screen). already → skip (guard blocked a double credit).
    if (res.status === "credited") {
      onComplete({
        match_gold_delta: result.goldEarned,
        duel_xp_after: res.progress.duel_xp,
        pve_trial_chests_delta: result.chestEarned ? 1 : 0,
        pve_best_stage: res.progress.pve_best_stage,
        pve_condition: res.progress.pve_condition,
      });
      if (result.chestEarned) onChestEarned();
    }
    setPveProgress(res.progress);
  }, [player, pveProgress, onComplete, onChestEarned]); // eslint-disable-line

  // Called by CombatScreen once the frame animation has fully played out.
  const handleCombatFinished = (stage: number, result: BossResult, sequence: CombatSequence) => {
    setPhase({ kind: "boss-result", stage, result });
    // Balance telemetry — every fight (win OR loss), best-effort, NEVER awaited.
    // Fires here (post-animation) so the background run_id has resolved.
    if (runState && player.profile_id) {
      logPveFight({
        profileId: player.profile_id,
        runId: pveProgress?.pve_run_id ?? null,
        tier: runState.tier,
        stage,
        playerLevel,
        playerStats,
        activeBoosts: runState.activeBonuses,
        sequence,
        variant: "control",
      });
    }
    // Persist the win immediately — Continue then only navigates.
    if (result.win && runState) void persistStageWin(stage, result, runState);
  };

  // Variant B — called by InteractiveCombatScreen when the live fight is over.
  // finalizeInteractiveCombat converts the fight into the control contract
  // (CombatSequence), so everything downstream is the shared path.
  const handleLiveCombatFinished = (stage: number, finalState: InteractiveCombatState) => {
    const sequence = finalizeInteractiveCombat(finalState);
    const result = sequence.result;
    setPhase({ kind: "boss-result", stage, result });
    if (runState && player.profile_id) {
      logPveFight({
        profileId: player.profile_id,
        runId: pveProgress?.pve_run_id ?? null,
        tier: runState.tier,
        stage,
        playerLevel,
        playerStats,
        activeBoosts: [], // no boosts in variant B
        sequence,
        variant: "spells",
        variantStats: interactiveVariantStats(finalState),
      });
    }
    if (result.win && runState) void persistStageWin(stage, result, runState);
  };

  // ── Run finalization (defeat OR stage-10 clear) — confirmed, never silent ──
  // NEVER credits any reward (savePveStageWin is the only credit path). It only
  // confirms the terminal DB state: resetPveRun on defeat, completePveTier on a
  // cleared tier. The run-result screen shows saving → saved, or error + Retry.
  const finalizeRun = useCallback(async (opts: {
    cleared: boolean;
    reachedStage: number;
    conditionAfter: number;
    totalXp: number;
    totalGold: number;
    chestEarned: boolean;
  }) => {
    const base = {
      kind: "run-result" as const,
      reachedStage: opts.reachedStage,
      cleared: opts.cleared,
      totalXp: opts.totalXp,
      totalGold: opts.totalGold,
      chestEarned: opts.chestEarned,
    };

    const profileId = player.profile_id;
    if (!profileId) { setPhase({ ...base, saved: true }); return; } // nothing to persist

    if (finalizingRef.current) return;
    finalizingRef.current = true;
    setPhase({ ...base, saving: true });

    const nowIso = new Date().toISOString();
    const idlePatch = {
      pve_current_stage: 1,
      pve_last_rewarded_stage: 0,
      pve_active_bonuses: [] as string[],
      pve_run_status: "idle",
      pve_run_id: null as string | null,
      pve_condition: opts.conditionAfter, // % décimal — jamais arrondi avant write
      pve_condition_updated_at: nowIso,
    };

    let ok: boolean;
    if (opts.cleared) {
      const tiers = await completePveTier(profileId, opts.conditionAfter);
      ok = tiers !== null;
      if (tiers) {
        setPveProgress((prev) => prev ? {
          ...prev,
          pve_current_tier: tiers.pve_current_tier,
          pve_best_tier: tiers.pve_best_tier,
          ...idlePatch,
        } : prev);
      }
    } else {
      ok = await resetPveRun(profileId, opts.conditionAfter);
      if (ok) setPveProgress((prev) => prev ? { ...prev, ...idlePatch } : prev);
    }

    finalizingRef.current = false;

    if (!ok) {
      retryFinalizeRef.current = () => void finalizeRun(opts);
      setPhase({
        ...base,
        error: opts.cleared
          ? "Tier progress not saved — check your connection and retry."
          : "Defeat not saved — check your connection and retry.",
        retryable: true,
      });
      return;
    }

    // History log only (pve_runs) — non-blocking, never credits anything.
    void insertPveRun({
      profileId,
      reachedStage: opts.reachedStage,
      cleared: opts.cleared,
      xpEarned: 0,
      goldEarned: 0,
      chestEarned: opts.cleared && opts.chestEarned ? 1 : 0,
      variant,
    });

    // Defeat: sync local condition only (no reward change).
    if (!opts.cleared) {
      onComplete({
        match_gold_delta: 0,
        duel_xp_after: null,
        pve_trial_chests_delta: 0,
        pve_best_stage: pveProgress?.pve_best_stage ?? (player.pve_best_stage ?? 0),
        pve_condition: opts.conditionAfter, // % décimal — jamais arrondi avant write
      });
    }

    retryFinalizeRef.current = null;
    setPhase({ ...base, saved: true });
  }, [player, pveProgress, onComplete]); // eslint-disable-line

  // ── After the boss result: credit THIS stage immediately, then advance ────
  // Each win is persisted+credited on its own (savePveStageWin). Anti-double-
  // reward lives in the service guard (run_id + current_stage + last_rewarded).
  const handleBossResultContinue = useCallback(async (stage: number, result: BossResult, run: RunState) => {
    const updatedRun: RunState = {
      ...run,
      condition: result.conditionAfter,
      totalXp: run.totalXp + result.xpEarned,
      totalGold: run.totalGold + result.goldEarned,
      chestEarned: run.chestEarned || result.chestEarned,
    };

    // Defeat → NO credit (stages 1..N-1 already credited on their win). The DB
    // reset MUST be confirmed (or retried) — never a silent fire-and-forget.
    if (!result.win) {
      setRunState(updatedRun);
      void finalizeRun({
        cleared: false,
        reachedStage: stage,
        conditionAfter: result.conditionAfter,
        totalXp: updatedRun.totalXp,
        totalGold: updatedRun.totalGold,
        chestEarned: updatedRun.chestEarned,
      });
      return;
    }

    // Win → the credit already started at combat end (persistStageWin). Here we
    // only make sure it landed (covers Retry and slow networks), then navigate.
    const key = `${run.tier}:${stage}`;
    if (stageSavedKeyRef.current !== key) {
      await persistStageWin(stage, result, run);
      if (stageSavedKeyRef.current !== key) return; // failed — error + Retry shown
    }
    const res = stageResRef.current;
    if (!res || res.status === "error") return; // defensive — should not happen

    setRunState(updatedRun);

    if (stage >= TOTAL_STAGES) {
      // Stage 10 cleared: rewards+chest already credited above (savePveStageWin
      // only — finalizeRun never re-credits). The tier advance MUST be confirmed
      // (or retried), otherwise the run would stay silently stuck at stage 10.
      //
      // 'already' can mean two things:
      //   - the run is still stuck 'active' with stage 10 rewarded (a previous
      //     session credited but never finalized) → finalize now (fixes it);
      //   - the run was already finalized elsewhere (idle) → nothing to do.
      const needsFinalize =
        res.status === "credited" ||
        (res.progress.pve_run_status === "active" && res.progress.pve_last_rewarded_stage >= TOTAL_STAGES);
      if (needsFinalize) {
        void finalizeRun({
          cleared: true,
          reachedStage: stage,
          conditionAfter: result.conditionAfter,
          totalXp: updatedRun.totalXp,
          totalGold: updatedRun.totalGold,
          chestEarned: updatedRun.chestEarned,
        });
      } else {
        setPhase({ kind: "run-result", reachedStage: stage, cleared: true, totalXp: updatedRun.totalXp, totalGold: updatedRun.totalGold, chestEarned: updatedRun.chestEarned, saved: true });
      }
    } else {
      setPhase({ kind: "camp", clearedStage: stage });
    }
  }, [player, pveProgress, persistStageWin, finalizeRun]); // eslint-disable-line

  // ── Camp "Continue Run" → launch next fight (optional bonus applied) ───────
  // Step 1 — CONFIRM the boost: apply it to the active bonuses and persist it,
  // WITHOUT launching the fight. The player then rests/decides before continuing.
  const handleConfirmBonus = useCallback((bonus: PveBonus, replaceIdx?: number, offered?: PveBoostId[]) => {
    if (!runState || phase.kind !== "camp") return;
    const before = runState.activeBonuses;
    const nextBonuses = applyBonusChoice(before, bonus, replaceIdx);
    setRunState((prev) => prev ? { ...prev, activeBonuses: nextBonuses } : prev);
    // Persist immediately (background) so the boost survives quit/refresh before
    // the next stage is won. It's re-written by savePveStageWin anyway.
    const profileId = player.profile_id;
    if (profileId) {
      void savePveBonusChoice(profileId, nextBonuses);
      setPveProgress((prev) => prev ? { ...prev, pve_active_bonuses: nextBonuses } : prev);
      // Boost pick-rate telemetry (offered vs chosen), best-effort.
      if (offered) {
        logPveBoostDraft({
          profileId,
          runId: pveProgress?.pve_run_id ?? null,
          tier: runState.tier,
          stage: phase.clearedStage,
          offered,
          chosen: bonus,
          replaced: replaceIdx !== undefined ? (before[replaceIdx] ?? null) : null,
          activeBefore: before,
        });
      }
    }
  }, [runState, phase, player, pveProgress]); // eslint-disable-line

  // Step 2 — CONTINUE: launch the next fight (any boost is already applied).
  const handleCampContinue = useCallback(() => {
    if (!runState || phase.kind !== "camp") return;
    // Recompute the LIVE regenerated condition at click time from the persisted
    // source (never the frozen post-fight value): the next fight must start with
    // whatever the player has recovered while resting at the camp. Same function +
    // source as the displayed bar, so combat can't diverge from what's shown.
    const ceiling = maxConditionWithBonuses(runState.activeBonuses);
    const liveCondition = (pveProgress?.pve_condition != null && pveProgress.pve_condition_updated_at)
      ? calculatePveCondition(pveProgress.pve_condition, pveProgress.pve_condition_updated_at, Date.now(), ceiling).condition
      : runState.condition;
    // Condition IS the player's HP — never launch a fight at 0 (recover first).
    if (liveCondition <= 0) return;
    const nextStage = phase.clearedStage + 1;
    const nextRun: RunState = { ...runState, stage: nextStage, condition: liveCondition };
    setRunState(nextRun);
    if (spellsVariant) {
      // No loadout mid-run (quit before picking / variant switched): offer the
      // spell-select first — never launch a live fight with zero spells.
      if (nextRun.spellLoadout.length === 0) { setPhase({ kind: "spell-select" }); return; }
      beginLiveFight(nextStage, nextRun);
    } else {
      beginFight(nextStage, nextRun);
    }
  }, [runState, phase, pveProgress, spellsVariant]); // eslint-disable-line

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  if (phase.kind === "pre-run") {
    return (
      <PreRunScreen
        player={player}
        conditionState={conditionState}
        maxHp={maxHpFromStats(playerStats.vitesse)}
        progress={pveProgress}
        // Start stays blocked until the players row (→ A/B variant, tier, stage)
        // is known; a fetch FAILURE unblocks it (offline must not lock the mode).
        waitingProgress={!!player.profile_id && !pveProgress && !progressFailed}
        onStart={startRun}
        onBack={onExit}
      />
    );
  }

  if (phase.kind === "spell-select") {
    return <SpellSelectScreen playerLevel={playerLevel} playerStats={playerStats} onConfirm={handleSpellsConfirmed} onBack={onExit} />;
  }

  if (phase.kind === "fighting-live" && runState) {
    const enc = getEncounter(runState.tier, phase.stage);
    // Sprite randomized per run (seed = run id); name/stats stay from the encounter.
    const opponentImg = pickEnemyArt(pveProgress?.pve_run_id ?? null, phase.stage, enc?.kind === "boss" ? "boss" : "mob");
    const opponentName = enc?.kind === "boss" ? (enc?.name ?? getEnemy(enc?.enemyId ?? "")?.name ?? "Boss") : "Minion";
    // 2v2 pets test — the equipped familiar is the ally pet (name + artwork);
    // no familiar equipped = a generic default companion, so everyone can test.
    // Inventory PNGs (trimmed to the pet) — the LAYER PNGs are full-canvas
    // overlays sized for the character frame, unusable as standalone sprites.
    const eq = (player.equipped ?? {}) as Record<string, string | undefined>;
    const familiarItem = eq.familiar ? (player.inventory || []).find((x) => x.iid === eq.familiar) : null;
    return (
      <InteractiveCombatScreen
        stage={phase.stage}
        initial={phase.combat}
        opponentImg={opponentImg}
        opponentName={opponentName}
        homeLayers={homeLayers}
        petImg={familiarItem ? INVENTORY_IMGS[familiarItem.name] ?? null : null}
        enemyPetImg={pickFamiliarArt(pveProgress?.pve_run_id ?? null, phase.stage)}
        onFinished={(finalState) => handleLiveCombatFinished(phase.stage, finalState)}
      />
    );
  }

  if (phase.kind === "fighting") {
    const enc = getEncounter(runState?.tier ?? 1, phase.stage);
    const opponentImg = pickEnemyArt(pveProgress?.pve_run_id ?? null, phase.stage, enc?.kind === "boss" ? "boss" : "mob");
    // Boss → its real name; regular enemy → generic "Minion".
    const opponentName = enc?.kind === "boss" ? (enc?.name ?? getEnemy(enc?.enemyId ?? "")?.name ?? "Boss") : "Minion";
    return (
      <CombatScreen
        stage={phase.stage}
        sequence={phase.sequence}
        opponentImg={opponentImg}
        opponentName={opponentName}
        activeBonuses={runState?.activeBonuses ?? []}
        homeLayers={homeLayers}
        onFinished={(result) => handleCombatFinished(phase.stage, result, phase.sequence)}
      />
    );
  }

  if (phase.kind === "boss-result" && runState) {
    return (
      <BossResultScreen
        stage={phase.stage}
        result={phase.result}
        run={runState}
        saving={stageSaving}
        error={stageError}
        onContinue={() => handleBossResultContinue(phase.stage, phase.result, runState)}
      />
    );
  }

  if (phase.kind === "camp" && runState) {
    return (
      <CampScreen
        clearedStage={phase.clearedStage}
        tier={runState.tier}
        activeBonuses={runState.activeBonuses}
        spellsVariant={spellsVariant}
        spellLoadout={runState.spellLoadout}
        playerLevel={playerLevel}
        condition={runState.condition}
        maxHp={maxHpFromStats(playerStats.vitesse)}
        playerStats={playerStats}
        energyBonus={energyBonus}
        onConfirmBonus={handleConfirmBonus}
        onContinue={handleCampContinue}
        onBack={onExit}
        resumed={phase.resumed ?? false}
      />
    );
  }

  if (phase.kind === "run-result") {
    return (
      <RunResultScreen
        phase={phase}
        bestStage={player.pve_best_stage ?? 0}
        onExit={onExit}
        onRetry={() => retryFinalizeRef.current?.()}
      />
    );
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-screens
// ─────────────────────────────────────────────────────────────────────────────

// Shared condition / HP meter — the SINGLE source of truth for how the player's
// life is displayed. Used by both PreRunScreen and CampScreen so the UI (and any
// future behaviour) stays in sync from one place. Purely presentational: the
// caller feeds the current values (live-ticking on pre-run; frozen mid-run at the
// camp, since that's the exact HP the next fight starts from).
function ConditionMeter({ conditionPct, condMax, maxHp, msToFull, isFull, loading }: {
  conditionPct: number; // current condition on the condMax scale (decimal)
  condMax: number;      // condition ceiling (maxConditionWithBonuses) → bar ratio
  maxHp: number;        // real HP ceiling (vitesse × 10, +bonuses) → HP figure
  msToFull: number;
  isFull: boolean;
  loading?: boolean;    // pre-run: Supabase fetch still in flight → show "…"
}) {
  const { st } = useST();
  const ratio = condMax > 0 ? Math.max(0, Math.min(1, conditionPct / condMax)) : 0;
  const hp = Math.round(ratio * maxHp);
  const pct = ratio * MAX_CONDITION;
  const col = loading ? C.muted : conditionColor(pct);
  const fill = conditionFill(pct); // green / gold / red by threshold
  const BAR_H = 20;
  return (
    <div style={{
      background: "linear-gradient(180deg,#121a2b,#0c1322)",
      borderRadius: 16, padding: 15, display: "flex", flexDirection: "column", gap: 10,
      border: `1px solid rgba(255,255,255,0.09)`, fontFamily: FONT.ui, clipPath: ST_BEVEL,
      boxShadow: !loading && isFull ? "0 0 24px rgba(45,227,107,0.10)" : "0 4px 14px rgba(0,0,0,0.25)",
    }}>
      <style>{`
        @keyframes stRegenPulse { 0%,100% { opacity: 0.3; } 50% { opacity: 0.9; } }
        /* Seamless flow: shift by EXACTLY one tile width (22px) so it loops with no jump. */
        @keyframes stRegenFlow { from { background-position: 0 0; } to { background-position: 22px 0; } }
        @keyframes stFullGlow { 0%,100% { opacity: 0.55; } 50% { opacity: 1; } }
      `}</style>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ font: `800 11px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", color: C.text, display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{ color: C.live, fontSize: 14 }}>❤</span> {st("health")}
        </span>
        <span style={{ font: `700 16px ${FONT.display}`, color: col }}>
          {loading ? "…" : st("hpValue", hp, maxHp)}
        </span>
      </div>
      {/* Thick condition gauge — gloss highlight + animated regen flow + leading-edge pulse */}
      <div style={{ position: "relative", height: BAR_H, borderRadius: BAR_H / 2, background: "rgba(0,0,0,0.42)", border: "1px solid rgba(255,255,255,0.06)", overflow: "hidden", boxShadow: "inset 0 2px 6px rgba(0,0,0,0.5)" }}>
        <div style={{
          position: "absolute", top: 0, bottom: 0, left: 0, width: `${ratio * 100}%`, borderRadius: BAR_H / 2,
          background: fill,
          boxShadow: isFull ? "0 0 10px rgba(45,227,107,0.5)" : "0 0 8px rgba(0,0,0,0.25)",
          transition: "width 0.5s cubic-bezier(0.22,1,0.36,1)",
        }}>
          {/* top gloss */}
          <div style={{ position: "absolute", top: 2, left: 4, right: 4, height: 4, borderRadius: 4, background: "linear-gradient(180deg, rgba(255,255,255,0.55), rgba(255,255,255,0))" }} />
          {/* animated diagonal flow while regenerating — one gradient tile of fixed
              width, repeated, shifted by exactly its width → truly seamless loop */}
          {!loading && !isFull && (
            <div style={{
              position: "absolute", inset: 0,
              backgroundImage: "linear-gradient(115deg, transparent 0 42%, rgba(255,255,255,0.20) 42% 58%, transparent 58% 100%)",
              backgroundSize: "22px 100%",
              backgroundRepeat: "repeat",
              animation: "stRegenFlow 0.9s linear infinite",
            }} />
          )}
        </div>
        {/* glowing regen head at the fill edge */}
        {!loading && !isFull && ratio > 0.02 && (
          <div style={{ position: "absolute", top: 0, bottom: 0, left: `calc(${ratio * 100}% - 6px)`, width: 12, borderRadius: 6, background: "rgba(255,190,190,0.9)", filter: "blur(3px)", animation: "stRegenPulse 1.2s ease-in-out infinite" }} />
        )}
      </div>
      {!loading && (
        <div style={{ font: `700 10px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: isFull ? C.turf : C.muted, textAlign: "right", animation: isFull ? "stFullGlow 2s ease-in-out infinite" : undefined }}>
          {isFull ? st("fullyRested") : st("fullIn", formatConditionTime(msToFull))}
        </div>
      )}
    </div>
  );
}

function PreRunScreen({ player, conditionState, maxHp, progress, waitingProgress, onStart, onBack }: {
  player: Player & { profile_id?: string };
  conditionState: ConditionState | null; // null while loading
  maxHp: number; // real HP ceiling derived from vitesse (1 vitesse = 10 HP)
  progress: PveProgress | null;
  waitingProgress: boolean; // players row still in flight → variant unknown, Start blocked
  onStart: () => void;
  onBack: () => void;
}) {
  const loaded = conditionState !== null && !waitingProgress;
  const canStart = loaded && conditionState!.condition > 0;
  const currentTier = progress?.pve_current_tier ?? player.pve_current_tier ?? 1;
  const currentTierName = getTier(currentTier)?.tierName ?? "Stadium Trial";
  const [showAllTiers, setShowAllTiers] = useState(false);
  // Resume when a run is active and already past stage 1.
  const resumeStage = progress?.pve_run_status === "active" ? (progress.pve_current_stage ?? 1) : 1;
  const isResume = progress?.pve_run_status === "active" && resumeStage > 1;

  return (
    <div style={{ padding: "18px 16px 26px", maxWidth: 480, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14, fontFamily: FONT.ui }}>
      <style>{ST_STYLE}</style>

      {/* Header — bevel tier banner (back button left, All tiers right) */}
      <div style={{ display: "flex", alignItems: "stretch", gap: 10 }}>
        <button onClick={onBack} style={{ flex: "none", alignSelf: "center", width: 38, height: 38, display: "grid", placeItems: "center", border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: C.text, borderRadius: 10, fontSize: 16, cursor: "pointer" }}>←</button>
        <div style={{ position: "relative", flex: 1, minWidth: 0, overflow: "hidden", borderRadius: 16, padding: "14px 14px 13px", background: "linear-gradient(135deg,#141d31,#0c1424)", border: "1px solid rgba(255,255,255,0.09)", clipPath: ST_BEVEL }}>
          <div style={{ position: "absolute", top: 0, right: 0, width: 150, height: "100%", background: "radial-gradient(120px 120px at 100% 0, rgba(245,179,1,0.18), transparent 70%)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", top: -1, left: 16, width: 64, height: 3, background: "linear-gradient(90deg,#F5B301,transparent)" }} />
          <div style={{ position: "relative", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <span style={{ display: "inline-block", font: `800 10px ${FONT.label}`, letterSpacing: 2, color: C.gold, background: "rgba(245,179,1,0.13)", border: "1px solid rgba(245,179,1,0.3)", borderRadius: 5, padding: "3px 8px" }}>Tier {currentTier}</span>
              <div style={{ font: `700 22px ${FONT.display}`, color: C.text, lineHeight: 1.05, marginTop: 8, textTransform: "uppercase", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{currentTierName}</div>
            </div>
            <button
              onClick={() => setShowAllTiers(true)}
              style={{ flex: "none", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.05)", color: C.text, borderRadius: 9, padding: "7px 10px", font: `700 10px ${FONT.label}`, letterSpacing: 1, cursor: "pointer", whiteSpace: "nowrap", textTransform: "uppercase" }}
            >
              All tiers
            </button>
          </div>
        </div>
      </div>

      {showAllTiers && <AllTiersModal currentTier={currentTier} bestTier={progress?.pve_best_tier ?? player.pve_best_tier ?? 1} onClose={() => setShowAllTiers(false)} />}

      {/* Run path — roguelike node rail (current stage highlighted) */}
      <RunPath tier={currentTier} currentStage={resumeStage} />

      {/* Condition / HP — live-ticking (regen recomputed each second by the parent) */}
      <ConditionMeter
        conditionPct={conditionState?.condition ?? 0}
        condMax={MAX_CONDITION}
        maxHp={maxHp}
        msToFull={conditionState?.msToFull ?? 0}
        isFull={conditionState?.isFull ?? false}
        loading={!conditionState}
      />

      {/* Rewards preview — full-run totals as game-style loot tiles */}
      {(() => {
        const stages = Array.from({ length: TOTAL_STAGES }, (_, i) => i + 1);
        const runXp = stages.reduce((s, st) => s + getStageRewards(st).xp, 0);
        const runGold = stages.reduce((s, st) => s + getStageRewards(st).gold, 0);
        const hasChest = stages.some((st) => getStageRewards(st).chest);
        return (
          <div style={{ position: "relative", overflow: "hidden", background: "linear-gradient(180deg,#121a2b,#0c1322)", borderRadius: 16, padding: 16, border: "1px solid rgba(255,255,255,0.08)", clipPath: ST_BEVEL }}>
            {/* shine sweep */}
            <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "45%", background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.08),transparent)", animation: "stShine 4.5s ease-in-out infinite", pointerEvents: "none" }} />
            <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 7, font: `800 11px ${FONT.label}`, letterSpacing: 2, color: C.muted }}>
                <span style={{ width: 5, height: 12, background: C.gold, borderRadius: 1, display: "inline-block" }} />RUN REWARDS
              </span>
              <span style={{ font: `600 10px ${FONT.ui}`, color: C.muted }}>full clear · all 10 stages</span>
            </div>
            <div style={{ position: "relative", display: "flex", gap: 10, justifyContent: "center" }}>
              <RewardTile icon="⚡" amount={`+${runXp}`} label="XP" contour="#48FF85" glow="#1AFF66" delay={0} />
              <RewardTile icon={<GoldIcon big />} amount={`+${runGold}`} label="Gold" contour="#FFE677" glow="#FFB700" delay={80} />
              {hasChest && (
                <RewardTile
                  icon={<img src={BOX.std} alt="" style={{ width: "78%", height: "78%", objectFit: "contain" }} />}
                  amount="×1" label="Pack" contour="#C9A2FF" glow="#8A5CFF" delay={160}
                />
              )}
            </div>
          </div>
        );
      })()}

      {/* CTA */}
      {loaded && !canStart && conditionState!.condition === 0 && (
        <div style={{ textAlign: "center", font: `600 13px ${FONT.ui}`, color: C.muted, padding: "8px 0" }}>
          No Condition — full regen in {formatConditionTime(conditionState!.msToFull)}.
        </div>
      )}
      <button
        onClick={canStart ? onStart : undefined}
        disabled={!canStart}
        style={{
          position: "relative", overflow: "hidden",
          background: canStart ? "linear-gradient(135deg,#FF7A2C,#FF2E44)" : "rgba(255,255,255,0.06)",
          border: "none", borderRadius: 14, padding: "16px 0",
          font: `800 18px ${FONT.display}`, letterSpacing: 1.5, textTransform: "uppercase",
          color: canStart ? "#fff" : C.muted, cursor: canStart ? "pointer" : "not-allowed",
          clipPath: ST_BEVEL,
          animation: canStart ? "stCtaGlow 2.6s ease-in-out infinite" : undefined,
        }}
      >
        {!loaded ? "Loading…" : canStart ? (isResume ? `▶️ Resume — Stage ${resumeStage}/${TOTAL_STAGES}` : "⚔️ Start Run") : "Unavailable"}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

// "All tiers" modal — MVP list built straight from the pveTiers.ts config.
// Shows tier number, name, recommended level, both boss checks (stage 5 & 10),
// and a locked / current / cleared status derived from the player's progress.
function AllTiersModal({ currentTier, bestTier, onClose }: {
  currentTier: number;
  bestTier: number;
  onClose: () => void;
}) {
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: C.bg ?? "#12151C", width: "100%", maxWidth: 480, maxHeight: "80vh", overflowY: "auto", borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: "20px 16px", display: "flex", flexDirection: "column", gap: 12 }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: C.text }}>All Tiers</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, fontSize: 22, cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button>
        </div>

        {PVE_TIERS.map((t) => {
          const status: "cleared" | "current" | "locked" =
            t.tier < currentTier ? "cleared" : t.tier === currentTier ? "current" : "locked";
          // Boss display name lives on the encounter (per-tier); fall back to the enemy catalogue.
          const boss5 = getEncounter(t.tier, 5)?.name ?? getEnemy(getEncounter(t.tier, 5)?.enemyId ?? "")?.name ?? "—";
          const boss10 = getEncounter(t.tier, 10)?.name ?? getEnemy(getEncounter(t.tier, 10)?.enemyId ?? "")?.name ?? "—";
          const statusColor = status === "cleared" ? C.turf : status === "current" ? C.gold : C.muted;
          const statusLabel = status === "cleared" ? "✓ Cleared" : status === "current" ? "▶ Current" : "🔒 Locked";
          return (
            <div key={t.tier} style={{
              background: C.surface,
              border: `1.5px solid ${status === "current" ? "rgba(245,179,1,0.4)" : "rgba(255,255,255,0.07)"}`,
              borderRadius: 16, padding: 14, display: "flex", flexDirection: "column", gap: 8,
              opacity: status === "locked" ? 0.7 : 1,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: C.muted }}>Tier {t.tier}</span>
                  <span style={{ fontSize: 16, fontWeight: 900, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.tierName}</span>
                </div>
                <span style={{ fontSize: 11, fontWeight: 800, color: statusColor, whiteSpace: "nowrap" }}>{statusLabel}</span>
              </div>
              {t.recommendedLevel && (
                <div style={{ fontSize: 12, color: C.muted }}>Recommended: <span style={{ color: C.text, fontWeight: 700 }}>{t.recommendedLevel}</span></div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1, background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>Boss · Stage 5</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{boss5}</div>
                </div>
                <div style={{ flex: 1, background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>Final · Stage 10</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.gold }}>{boss10}</div>
                </div>
              </div>
            </div>
          );
        })}

        {bestTier > 0 && (
          <div style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 4 }}>
            More tiers coming soon.
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

// Turn-by-turn combat animation. Plays the pre-computed CombatSequence frame by
// frame (simulateCombat is called ONCE in the parent, never here/in render). The
// decisive outcome is always sequence.result — the frames only interpolate HP.
const COMBAT_FRAME_MS = 950; // slower cadence — time to read each action / number
// Interactive (variant B): the enemy resolves its whole turn at once (e.g. a boss
// DOUBLE strike). We reveal its frames one at a time with this beat so both hits
// are actually seen instead of collapsing into the final HP snapshot.
const ENEMY_STEP_MS = 780;

// Arena layout — fixed zones so both fighters share the exact same structure.
// HP bars live in their own fixed-height row (never anchored to the character),
// and both characters sit in a fixed-height box, bottom-aligned on one baseline.
const ARENA_CHAR_H = 300;   // character-box height (identical for both fighters) — big full-body figures
export const ARENA_VS_W = 48;      // center VS column width
const ARENA_HP_H = 34;      // fixed HP-area height → both bars sit on the same level
const PLAYER_SCALE = 1;     // sprites are normalized to one canvas → both sides render at the exact same size
export const DMG_OFFSET = 34;      // px nudge of the central damage number toward the hit target
// ── 2v2 pets — shared fighter stage ──────────────────────────────────────────
// One IDENTICAL square reference box for both sides. The character lives inside
// (scaled freely, overflow visible — an oversized mob is never cropped); the pet
// is anchored to the BOX, not the character, so both pets share the exact same
// size and spot, mirrored. Tune the two constants below.
// Hit/lunge animations are PER ACTOR: the master attacks → the pet stays still,
// the pet attacks → it lunges exactly like the master would.
const PET_HEIGHT_PERCENT = 35;  // pet height, % of the reference box
const PET_INSET = "20%";       // horizontal offset toward the VS (more negative = closer to center)

export function FighterStage({ side, charScale = 1, dimChar, deadChar, charShaking, charAttacking, petShaking, petAttacking, shakeIdx, pet, petLayer, children }: {
  side: "ally" | "enemy";
  charScale?: number;           // character-only zoom (the pet ignores it)
  dimChar?: boolean;            // master faded while its pet is playing
  deadChar?: boolean;           // master down but the fight goes on (2v2)
  charShaking?: boolean;        // the MASTER is being hit this frame
  charAttacking?: boolean;      // the MASTER is acting this frame (lunge)
  petShaking?: boolean;         // the PET is being hit this frame
  petAttacking?: boolean;       // the PET is acting this frame (lunge)
  shakeIdx?: number;            // retrigger key for the shake animation
  pet?: { img: string | null; hp: number; maxHp: number; dead: boolean; active?: boolean } | null;
  // Layer mode: the pet artwork is a FULL-CANVAS layer authored to fit the
  // character canvas (like a Profil calque), so it overlays the master box at
  // full size (mirrored for the enemy) instead of sitting as a foot sprite.
  petLayer?: boolean;
  children: React.ReactNode;    // the character visual
}) {
  const lunge = `stLunge${side === "ally" ? "R" : "L"} 0.28s cubic-bezier(0.22,1,0.36,1)`;
  const shake = side === "ally"
    ? `stKnockL${(shakeIdx ?? 0) % 2} 0.2s ease-out 0.08s both`
    : `stKnockR${(shakeIdx ?? 0) % 2} 0.2s ease-out 0.08s both`;
  return (
    <div style={{ position: "relative", height: "100%", aspectRatio: "1 / 1", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      {/* Character — animation on the OUTER div (the keyframes own `transform`,
          they would cancel the scale), scale/fade on the inner one. */}
      <div style={{ position: "relative", zIndex: 1, height: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", animation: charShaking ? shake : charAttacking ? lunge : undefined }}>
        <div style={{ height: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", transformOrigin: "bottom center", transform: `scale(${charScale})`, opacity: dimChar ? 0.45 : deadChar ? 0.3 : 1, filter: deadChar ? "grayscale(1)" : undefined, transition: "opacity .25s" }}>
          {children}
        </div>
      </div>
      {/* Pet — LAYER mode: full-canvas calque overlaying the master box (mirrored
          for the enemy), authored to sit beside the character. Otherwise a small
          foot sprite toward the INSIDE (the VS), in front. */}
      {pet && petLayer && (
        <div style={{ position: "absolute", inset: 0, zIndex: 2, display: "flex", alignItems: "flex-end", justifyContent: "center", animation: petShaking ? shake : petAttacking ? lunge : undefined }}>
          <div style={{ height: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", opacity: pet.dead ? 0.25 : 1, filter: pet.dead ? "grayscale(1)" : undefined, transform: pet.active ? "scale(1.05)" : undefined, transformOrigin: "bottom center", transition: "opacity .25s, transform .25s" }}>
            {pet.img
              ? <img src={pet.img} alt="" style={{ height: "100%", width: "auto", objectFit: "contain", transform: side === "enemy" ? "scaleX(-1)" : undefined }} />
              : <span style={{ fontSize: 54, lineHeight: 1 }}>🐾</span>}
          </div>
        </div>
      )}
      {pet && !petLayer && (
        <div style={{ position: "absolute", bottom: 0, ...(side === "ally" ? { right: PET_INSET } : { left: PET_INSET }), height: `${PET_HEIGHT_PERCENT}%`, zIndex: 2, animation: petShaking ? shake : petAttacking ? lunge : undefined }}>
          <div style={{ height: "100%", display: "flex", alignItems: "flex-end", opacity: pet.dead ? 0.25 : 1, filter: pet.dead ? "grayscale(1)" : undefined, transform: pet.active ? "scale(1.1)" : undefined, transformOrigin: "bottom center", transition: "opacity .25s, transform .25s" }}>
            {pet.img
              ? <img src={pet.img} alt="" style={{ height: "100%", width: "auto", objectFit: "contain", transform: side === "enemy" ? "scaleX(-1)" : undefined }} />
              : <span style={{ fontSize: 54, lineHeight: 1 }}>🐾</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Turn-order bar (2v2) ──────────────────────────────────────────────────────
// One mini card per combatant in play order (you → enemy → your pet → its pet):
// HP sliver on top (green allies / red enemies), icon below. A white arrow
// SLIDES under the active card. Absolutely centered by the caller.
const TURN_CARD_W = 26;
const TURN_CARD_H = 36;
const TURN_CARD_GAP = 5;

function TurnOrderBar({ active, slots }: {
  active: number; // index of the acting combatant
  slots: { icon: string; ally: boolean; hp: number; maxHp: number }[];
}) {
  return (
    <div style={{ position: "relative", display: "flex", gap: TURN_CARD_GAP }}>
      {slots.map((s, i) => {
        const ratio = Math.min(1, Math.max(0, s.maxHp > 0 ? s.hp / s.maxHp : 0));
        const dead = s.hp <= 0;
        return (
          <div key={i} style={{ width: TURN_CARD_W, height: TURN_CARD_H, borderRadius: 8, border: `1px solid rgba(255,255,255,${i === active ? 0.35 : 0.14})`, background: "rgba(255,255,255,0.04)", display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 4, gap: 3, opacity: dead ? 0.3 : i === active ? 1 : 0.7, filter: dead ? "grayscale(1)" : undefined, transition: "border-color .3s, opacity .3s" }}>
            <div style={{ width: TURN_CARD_W - 10, height: 4, borderRadius: 2, background: "rgba(0,0,0,0.5)", overflow: "hidden" }}>
              <div style={{ width: `${ratio * 100}%`, height: "100%", borderRadius: 2, background: s.ally ? "linear-gradient(180deg,#45E8A0,#16A85C)" : "linear-gradient(180deg,#FF6B6B,#FF3B47)", transition: "width .35s ease" }} />
            </div>
            <span style={{ fontSize: 13, lineHeight: 1 }}>{s.icon}</span>
          </div>
        );
      })}
      {/* White arrow — slides under the acting card */}
      <span style={{ position: "absolute", bottom: -8, left: active * (TURN_CARD_W + TURN_CARD_GAP) + TURN_CARD_W / 2, transform: "translateX(-50%)", width: 13, height: 8, background: "#fff", clipPath: "polygon(50% 0, 100% 100%, 0 100%)", filter: "drop-shadow(0 0 6px rgba(255,255,255,0.8))", transition: "left .35s ease" }} />
    </div>
  );
}

// One fighter = one stable column: fixed HP area on top, fixed-height character
// box (bottom-aligned, centered), then the name. Both fighters share the exact
// same structure so bars and baselines always line up.
export function FighterColumn({ hp, maxHp, shield = 0, tone = "enemy", name, shaking, attacking, lungeDir, shakeIdx, dead, petBar, children }: {
  hp: number;
  maxHp: number;
  shield?: number;               // player shield overlay (0 = none)
  tone?: "player" | "enemy";     // HP bar colour (player = green, enemy = red)
  name: string;
  shaking: boolean;              // this fighter is being HIT this frame (hit react)
  attacking: boolean;            // this fighter is ATTACKING this frame (lunge)
  lungeDir: "left" | "right";    // lunge direction, toward the opponent
  shakeIdx: number;
  dead: boolean;
  /** 2v2 pets — a compact pet HP bar stacked ABOVE the master's. */
  petBar?: { hp: number; maxHp: number };
  children: React.ReactNode;
}) {
  return (
    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      {/* HP area — fixed height, bar(s) anchored to the bottom of the zone */}
      <div style={{ height: petBar ? ARENA_HP_H + 16 : ARENA_HP_H, width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", gap: 3 }}>
        {petBar && <HpBar value={petBar.hp} max={petBar.maxHp} tone={tone} compact />}
        <HpBar value={hp} max={maxHp} shield={shield} tone={tone} />
      </div>
      {/* Character box — fixed height, bottom baseline, horizontally centered */}
      <div
        style={{
          height: ARENA_CHAR_H,
          width: "100%",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          animation: shaking
            ? `${lungeDir === "right" ? "stKnockL" : "stKnockR"}${shakeIdx % 2} 0.2s ease-out 0.08s both`
            : attacking
              ? `stLunge${lungeDir === "right" ? "R" : "L"} 0.28s cubic-bezier(0.22,1,0.36,1)`
              : undefined,
          opacity: dead ? 0.35 : 1,
          transition: "opacity 0.3s",
        }}
      >
        {children}
      </div>
      {/* Name area */}
      <div style={{ font: `700 10px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted }}>{name}</div>
    </div>
  );
}

function CombatScreen({ stage, sequence, opponentImg, opponentName, activeBonuses, homeLayers, onFinished }: {
  stage: number;
  sequence: CombatSequence;
  opponentImg: string; // encounter enemy artwork (bosses have their own; mobs use the placeholder)
  opponentName: string; // boss name, or generic "Minion" for regular enemies
  activeBonuses: PveBoostId[]; // active boosts (mini chips shown at the bottom)
  homeLayers?: AvatarLayers;
  onFinished: (result: BossResult) => void;
}) {
  const { frames, result, playerMaxHp, bossMaxHp } = sequence;

  const [idx, setIdx] = useState(-1); // index of the last applied frame
  const [pHp, setPHp] = useState(frames[0]?.playerHp ?? playerMaxHp);
  const [bHp, setBHp] = useState(bossMaxHp);
  const [pShield, setPShield] = useState(frames[0]?.playerShield ?? 0);
  const [hitTarget, setHitTarget] = useState<CombatActor | null>(null); // who just got hit
  const finishedRef = useRef(false);

  useEffect(() => {
    let i = 0;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const playNext = () => {
      if (i >= frames.length) {
        // Hold the final HP a beat, then hand off to the result screen (once).
        timers.push(setTimeout(() => {
          if (!finishedRef.current) { finishedRef.current = true; onFinished(result); }
        }, COMBAT_FRAME_MS));
        return;
      }
      const f = frames[i];
      setIdx(i);
      setPHp(f.playerHp);
      setBHp(f.bossHp);
      setPShield(f.playerShield);
      setHitTarget(f.attacker === "player" ? "boss" : "player");
      i += 1;
      timers.push(setTimeout(playNext, COMBAT_FRAME_MS));
    };
    playNext();
    return () => { timers.forEach(clearTimeout); }; // cleanup on unmount / sequence change
  }, [sequence]); // eslint-disable-line

  const current = idx >= 0 ? frames[idx] : null;

  // Skip: jump straight to the decided outcome (same result the animation lands on;
  // finishedRef guards against a double onFinished). No re-roll — presentation only.
  const skip = () => { if (!finishedRef.current) { finishedRef.current = true; onFinished(result); } };

  return (
    <div style={{ padding: "20px 16px", maxWidth: 480, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18, minHeight: "60vh", fontFamily: FONT.ui }}>
      <style>{ST_STYLE}</style>
      <style>{`
        @keyframes stKnockL0 { 0%,100% { transform: translateX(0); } 35% { transform: translateX(-8px); } }
        @keyframes stKnockL1 { 0%,100% { transform: translateX(0); } 35% { transform: translateX(-8px); } }
        @keyframes stKnockR0 { 0%,100% { transform: translateX(0); } 35% { transform: translateX(8px); } }
        @keyframes stKnockR1 { 0%,100% { transform: translateX(0); } 35% { transform: translateX(8px); } }
        @keyframes stDmgPop { 0% { opacity: 0; transform: translateY(8px); } 12% { opacity: 1; } 78% { opacity: 1; transform: translateY(-6px); } 100% { opacity: 0; transform: translateY(-16px); } }
        @keyframes stLungeR { 0%,100% { transform: translateX(0); } 40% { transform: translateX(18px); } }
        @keyframes stLungeL { 0%,100% { transform: translateX(0); } 40% { transform: translateX(-18px); } }
        @keyframes stProjFlyR { from { transform: translateX(-55px); opacity: 0.9; } 20% { opacity: 1; } 75% { opacity: 1; } to { transform: translateX(55px); opacity: 0; } }
        @keyframes stProjFlyL { from { transform: translateX(55px); opacity: 0.9; } 20% { opacity: 1; } 75% { opacity: 1; } to { transform: translateX(-55px); opacity: 0; } }
      `}</style>

      <StageBar current={stage} total={TOTAL_STAGES} />

      <div style={{ textAlign: "center", font: `700 16px ${FONT.display}`, letterSpacing: 1.5, textTransform: "uppercase", color: C.text }}>Stage {stage} Boss</div>

      {/* Arena — two identical FighterColumns (HP area → character box → name)
          around a central VS zone. HP areas are fixed-height so both bars stay
          on the same level; character boxes share one fixed height and a common
          bottom baseline. The damage number floats in the central zone, offset
          toward the hit target. */}
      <div style={{ position: "relative", overflow: "hidden", borderRadius: 16, padding: "14px 10px 12px", background: "radial-gradient(300px 200px at 50% 120%, rgba(45,227,107,0.10), transparent 70%), linear-gradient(180deg,#0c1626,#070d18)", border: "1px solid rgba(255,255,255,0.07)" }}>
        {/* cyan arena grid + floor line */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "repeating-linear-gradient(0deg, rgba(76,201,240,0.05) 0 1px, transparent 1px 26px)" }} />
        <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "flex-end", gap: 8 }}>
        {/* Player column */}
        <FighterColumn
          hp={pHp}
          maxHp={playerMaxHp}
          shield={pShield}
          tone="player"
          name="You"
          shaking={hitTarget === "player" && !!current}
          attacking={hitTarget === "boss" && !!current}
          lungeDir="right"
          shakeIdx={idx}
          dead={pHp <= 0}
        >
          <div
            style={{
              height: "100%",
              aspectRatio: "1 / 1",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              transformOrigin: "bottom center",
              transform: `scale(${PLAYER_SCALE})`,
            }}
          >
            <LayeredCharacter
              layers={homeLayers}
              glowColor={hitTarget === "boss" ? "rgba(45,227,107,0.9)" : undefined}
              style={{ height: "100%", width: "auto" }}
            />
          </div>
        </FighterColumn>

        {/* Central zone — VS marker + floating damage, offset toward the target */}
        <div style={{ width: ARENA_VS_W, flexShrink: 0, alignSelf: "center", position: "relative", display: "flex", justifyContent: "center" }}>
          <div style={{ font: `700 13px ${FONT.display}`, letterSpacing: 1, color: "#4CC9F0", textShadow: "0 0 10px rgba(76,201,240,0.6)" }}>VS</div>
          {/* Projectile — flies from attacker toward the defender */}
          {current && current.damage > 0 && hitTarget && (
            <div
              key={`proj-${idx}`}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: -10,
                marginLeft: -10,
                width: 20,
                height: 20,
                fontSize: 16,
                lineHeight: "20px",
                textAlign: "center",
                animation: hitTarget === "player"
                  ? "stProjFlyL 0.22s ease-out both"
                  : "stProjFlyR 0.22s ease-out both",
                pointerEvents: "none",
                zIndex: 10,
              }}
            >
              ⚽
            </div>
          )}
          {current && current.damage > 0 && hitTarget && (
            <div
              key={`dmg-${idx}`}
              style={{
                position: "absolute",
                top: -26,
                // Nudge toward whoever takes the hit: player = left, boss = right.
                ...(hitTarget === "player"
                  ? { right: "50%", marginRight: DMG_OFFSET }
                  : { left: "50%", marginLeft: DMG_OFFSET }),
                font: `700 ${current.crit ? 22 : 18}px ${FONT.display}`,
                color: current.crit ? C.gold : C.live,
                animation: "stDmgPop 0.85s ease-out both",
                pointerEvents: "none",
                whiteSpace: "nowrap",
                textAlign: "center",
              }}
            >
              {current.crit && <div style={{ font: `700 10px ${FONT.label}`, letterSpacing: 1, color: C.gold }}>CRIT!</div>}
              -{current.damage}
            </div>
          )}
          {/* Heal proc / Last Stand — floating cues on the player's side */}
          {current && current.heal ? (
            <div key={`heal-${idx}`} style={{ position: "absolute", top: -26, right: "50%", marginRight: DMG_OFFSET, fontSize: 16, fontWeight: 900, color: C.turf, animation: "stDmgPop 0.85s ease-out both", pointerEvents: "none", whiteSpace: "nowrap" }}>
              +{current.heal}💚
            </div>
          ) : null}
          {current && current.lastStand ? (
            <div key={`ls-${idx}`} style={{ position: "absolute", top: -26, right: "50%", marginRight: DMG_OFFSET, fontSize: 13, fontWeight: 900, color: "#4CC9F0", animation: "stDmgPop 0.85s ease-out both", pointerEvents: "none", whiteSpace: "nowrap" }}>
              🔰 SHIELD
            </div>
          ) : null}
          {current && current.counter ? (
            <div key={`ctr-${idx}`} style={{ position: "absolute", top: -46, left: "50%", marginLeft: DMG_OFFSET, fontSize: 12, fontWeight: 900, color: "#F5B301", animation: "stDmgPop 0.85s ease-out both", pointerEvents: "none", whiteSpace: "nowrap" }}>
              ↩ RIPOSTE
            </div>
          ) : null}
        </div>

        {/* Opponent column */}
        <FighterColumn
          hp={bHp}
          maxHp={bossMaxHp}
          tone="enemy"
          name={opponentName}
          shaking={hitTarget === "boss" && !!current}
          attacking={hitTarget === "player" && !!current}
          lungeDir="left"
          shakeIdx={idx}
          dead={bHp <= 0}
        >
          <img
            src={opponentImg}
            alt="Opponent"
            style={{ display: "block", height: "100%", width: "auto", objectFit: "contain", filter: hitTarget === "player" ? "drop-shadow(0 0 10px rgba(255,59,71,0.9))" : undefined, transition: "filter .18s ease-out" }}
          />
        </FighterColumn>
        </div>
      </div>

      {/* Active boosts (mini chips) + Skip */}
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, font: `800 10px ${FONT.label}`, letterSpacing: 2, textTransform: "uppercase", color: C.muted }}>
          <span style={{ width: 5, height: 12, background: "#4CC9F0", borderRadius: 1, display: "inline-block" }} />Active boosts
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            {activeBonuses.length === 0 ? (
              <span style={{ font: `600 11px ${FONT.ui}`, color: C.faint }}>No active boosts yet</span>
            ) : (
              activeBonuses.map((b, i) => (
                <span key={i} title={boostName(b)} style={{ flex: "none", width: 28, height: 28, display: "grid", placeItems: "center", borderRadius: 8, background: boostBg(b), border: `1px solid ${boostColor(b)}44`, fontSize: 15 }}>{boostIcon(b)}</span>
              ))
            )}
          </div>
          <button onClick={skip} style={{ flex: "none", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.05)", color: C.muted, borderRadius: 9, padding: "8px 14px", font: `700 10px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer" }}>SKIP ⏭</button>
        </div>
      </div>
    </div>
  );
}

// Combat HP bar: red health, plus a distinct BLUE shield segment appended to the
// right of the health — a genuine "extra life bar". Shield is absorbed before HP,
// and (since the fill depletes right-to-left) the blue segment drains first, then
// the red. Health + shield are clamped to the track width.
export function HpBar({ value, max, shield = 0, tone = "enemy", align = "center", compact = false }: { value: number; max: number; shield?: number; tone?: "player" | "enemy"; align?: "left" | "center" | "right"; compact?: boolean }) {
  const hpRatio = Math.min(1, Math.max(0, max > 0 ? value / max : 0));
  const shieldRatio = Math.min(1 - hpRatio, Math.max(0, max > 0 ? shield / max : 0));
  const hpFill = tone === "player" ? "linear-gradient(180deg,#45E8A0,#16A85C)" : "linear-gradient(180deg,#FF6B6B,#FF3B47)";
  const BAR_H = compact ? 7 : 12;
  const barTrack = (
    <div style={{ position: "relative", flex: compact ? 1 : undefined, height: BAR_H, background: "rgba(0,0,0,0.42)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: BAR_H / 2, overflow: "hidden", boxShadow: "inset 0 1px 3px rgba(0,0,0,0.5)" }}>
      {/* Health */}
      <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: `${hpRatio * 100}%`, background: hpFill, boxShadow: tone === "player" ? "0 0 8px rgba(45,227,107,0.4)" : "0 0 8px rgba(255,59,71,0.35)", transition: "width 0.35s ease" }}>
        {!compact && <div style={{ position: "absolute", top: 1, left: 3, right: 3, height: 3, borderRadius: 3, background: "linear-gradient(180deg,rgba(255,255,255,0.5),transparent)" }} />}
      </div>
      {/* Shield (blue) — appended right after the health */}
      {shield > 0 && (
        <div style={{ position: "absolute", top: 0, bottom: 0, left: `${hpRatio * 100}%`, width: `${shieldRatio * 100}%`, background: "linear-gradient(180deg, #7DD9F5, #3BA7E0)", boxShadow: "0 0 8px rgba(76,201,240,0.6)", transition: "width 0.35s ease, left 0.35s ease" }} />
      )}
    </div>
  );
  // Pet bar (compact): total sits inline AFTER the bar. Main bar: current HP only,
  // below the track — no "/ max" to keep the interface clean.
  if (compact) {
    return (
      <div style={{ width: "100%", maxWidth: 110, display: "flex", alignItems: "center", gap: 5 }}>
        {barTrack}
        <span style={{ font: `700 9px ${FONT.display}`, color: C.muted, whiteSpace: "nowrap", flexShrink: 0 }}>{Math.round(value)}/{max}</span>
      </div>
    );
  }
  return (
    <div style={{ width: "100%", maxWidth: 150 }}>
      {barTrack}
      <div style={{ font: `700 10px ${FONT.display}`, color: C.muted, textAlign: align, marginTop: 3 }}>
        {Math.round(value)}{shield > 0 && <span style={{ color: "#4CC9F0", fontWeight: 800 }}> +{Math.round(shield)}🛡</span>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function BossResultScreen({ stage, result, run, saving, error, onContinue }: {
  stage: number;
  result: BossResult;
  run: RunState;
  saving: boolean;
  error: string | null;
  onContinue: () => void;
}) {
  const isLastStage = stage === TOTAL_STAGES;
  const cleared = result.win && isLastStage;
  const isDead = !result.win;
  const idleLabel = cleared ? "🏆 Claim Rewards" : isDead ? "💀 End Run" : "Continue →";
  const btnLabel = saving ? "Saving stage…" : error ? "↻ Retry" : idleLabel;
  // Main title + subtext (wording only — no reward logic here).
  const mainTitle = result.win ? "Success" : "Defeat";
  const subTitle = cleared ? "Stadium cleared — all 10 stages!" : result.win ? `Stage ${stage} cleared` : `Reached Stage ${stage}`;

  const accent = cleared ? C.gold : result.win ? C.turf : C.live;
  return (
    <div style={{ padding: "20px 16px 26px", maxWidth: 480, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14, fontFamily: FONT.ui }}>
      <style>{ST_STYLE}</style>
      <style>{`@keyframes stSpin { to { transform: rotate(360deg); } }`}</style>

      <StageBar current={stage} total={TOTAL_STAGES} won={result.win} />

      {/* Result banner */}
      <div style={{
        background: `linear-gradient(180deg, ${accent}18, rgba(12,19,34,0.4))`,
        border: `1.5px solid ${accent}55`,
        borderRadius: 20, padding: "24px 16px", textAlign: "center", display: "flex", flexDirection: "column", gap: 8,
        clipPath: ST_BEVEL, animation: "stBanner 0.45s cubic-bezier(0.34,1.56,0.64,1) both",
        boxShadow: `0 0 26px ${accent}22`,
      }}>
        <div style={{ fontSize: 46 }}>{cleared ? "🏆" : result.win ? "✅" : "💀"}</div>
        <div style={{ font: `700 26px ${FONT.display}`, letterSpacing: 2, textTransform: "uppercase", color: accent, textShadow: `0 0 22px ${accent}66` }}>{mainTitle}</div>
        <div style={{ font: `600 12px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: C.muted }}>{subTitle}</div>
      </div>

      {/* Damage taken only — condition figure removed (misleading on this screen). */}
      <div style={{ background: "linear-gradient(180deg,#121a2b,#0c1322)", borderRadius: 16, padding: "14px 16px", border: "1px solid rgba(255,255,255,0.08)", clipPath: ST_BEVEL }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ font: `700 11px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted }}>Damage taken</span>
          <span style={{ font: `700 15px ${FONT.display}`, color: C.live }}>-{result.damageReceived}</span>
        </div>
      </div>

      {/* Stage rewards (win only) — values read straight from BossResult */}
      {result.win && (
        <div style={{ background: "linear-gradient(180deg,#121a2b,#0c1322)", borderRadius: 16, padding: 16, border: "1px solid rgba(255,255,255,0.08)", clipPath: ST_BEVEL }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14, font: `800 11px ${FONT.label}`, letterSpacing: 2, color: C.muted }}>
            <span style={{ width: 5, height: 12, background: C.gold, borderRadius: 1, display: "inline-block" }} />STAGE REWARDS
          </div>
          <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
            <RewardBadge icon="⚡" label="XP" value={`+${result.xpEarned}`} />
            <RewardBadge icon={<GoldIcon />} label="Gold" value={`+${result.goldEarned}`} />
          </div>
        </div>
      )}

      {/* Bonus chest — only when the stage-10 boss is beaten */}
      {cleared && result.chestEarned && (
        <div style={{ background: "rgba(245,179,1,0.10)", border: "1.5px solid rgba(245,179,1,0.4)", borderRadius: 16, padding: "16px", textAlign: "center", display: "flex", flexDirection: "column", gap: 6, clipPath: ST_BEVEL }}>
          <div style={{ fontSize: 34 }}>📦</div>
          <div style={{ font: `700 15px ${FONT.display}`, letterSpacing: 0.5, color: C.gold }}>Bonus Chest earned!</div>
          <div style={{ font: `500 12px ${FONT.ui}`, color: C.muted }}>Reward for clearing the final boss.</div>
        </div>
      )}

      {/* Run totals (shown on last stage or death) */}
      {(isDead || isLastStage) && (run.totalXp > 0 || run.totalGold > 0) && (
        <div style={{ background: "rgba(245,179,1,0.06)", borderRadius: 16, padding: 16, border: "1px solid rgba(245,179,1,0.18)", clipPath: ST_BEVEL }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12, font: `800 11px ${FONT.label}`, letterSpacing: 2, color: C.gold }}>
            <span style={{ width: 5, height: 12, background: C.gold, borderRadius: 1, display: "inline-block" }} />RUN TOTAL
          </div>
          <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
            <RewardBadge icon="⚡" label="XP" value={`+${run.totalXp + result.xpEarned}`} />
            <RewardBadge icon={<GoldIcon />} label="Gold" value={`+${run.totalGold + result.goldEarned}`} />
          </div>
        </div>
      )}

      {error && (
        <div style={{ background: "rgba(255,59,71,0.08)", border: "1px solid rgba(255,59,71,0.25)", borderRadius: 12, padding: "10px 14px", font: `500 12px ${FONT.ui}`, color: C.live, textAlign: "center" }}>
          {error}
        </div>
      )}

      <button
        onClick={saving ? undefined : onContinue}
        disabled={saving}
        style={{
          background: saving ? "rgba(255,255,255,0.06)" : result.win ? "linear-gradient(135deg,#2DE36B,#159B52)" : "rgba(255,255,255,0.06)",
          border: result.win || saving ? "none" : "1px solid rgba(255,255,255,0.12)",
          borderRadius: 14, padding: "16px 0",
          font: `800 16px ${FONT.display}`, letterSpacing: 1.5, textTransform: "uppercase",
          color: saving ? C.muted : "#fff", cursor: saving ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10, clipPath: ST_BEVEL,
        }}
      >
        {saving && (
          <span style={{ display: "inline-block", width: 15, height: 15, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "stSpin 0.7s linear infinite" }} />
        )}
        {btnLabel}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

// Intermediate "camp" screen shown after each cleared boss (win, non-final):
// regen info + active boosts + optional bonus pick when the cleared stage is a
// bonus stage (1,3,5,7,9). No DB write here — run is only committed at the end.
function CampScreen({ clearedStage, tier, activeBonuses, spellsVariant, spellLoadout, playerLevel, condition, maxHp, playerStats, energyBonus, onConfirmBonus, onContinue, onBack, resumed }: {
  clearedStage: number;
  tier: number; // current tier — to read the next encounter (boss preview)
  activeBonuses: PveBonus[];
  spellsVariant: boolean;      // variant B: no boost draft — camp is rest-only
  spellLoadout: PveSpellId[];  // variant B: the run's locked spells (info display)
  playerLevel: number;         // variant B: concrete numbers in the spell info popup
  condition: number; // live decimal condition (regen-ticked by the parent)
  maxHp: number; // real HP ceiling derived from vitesse
  playerStats: EquipmentStats; // base item stats, for the stat preview
  energyBonus?: number;        // ball affix — shown in the stat preview (energy/turn)
  onConfirmBonus: (bonus: PveBoostId, replaceIdx?: number, offered?: PveBoostId[]) => void; // step 1: apply the boost
  onContinue: () => void; // step 2: launch the next fight
  onBack: () => void; // leave the camp (run stays active + persisted; regen keeps going)
  resumed: boolean;  // reached via resume-to-camp (not a fresh camp after a win)
}) {
  const { st, lang } = useST();
  const nextStage = clearedStage + 1;
  // Look ahead from the next stage: the next MINION and the next BOSS may be
  // different stages (e.g. cleared 4 → next boss is stage 5, next minion is 6).
  // Both get their own clickable "Check Stats" card so the player can plan.
  const findAhead = (kind: "mob" | "boss"): number | undefined => {
    for (let s = nextStage; s <= TOTAL_STAGES; s++) {
      if (getEncounter(tier, s)?.kind === kind) return s;
    }
    return undefined;
  };
  const nextMinionStage = findAhead("mob");
  const nextBossStage = findAhead("boss");
  const bossEnc = nextBossStage ? getEncounter(tier, nextBossStage) : undefined;
  const bossName = bossEnc?.name ?? getEnemy(bossEnc?.enemyId ?? "")?.name ?? "—";
  const bossIsFinal = nextBossStage === TOTAL_STAGES;

  // Which enemy's stats the "Check Stats" popup is showing (by stage), if any.
  const [statPopupStage, setStatPopupStage] = useState<number | null>(null);

  // Whether this camp's boost has already been committed (this session).
  const [confirmed, setConfirmed] = useState(false);
  // On a fresh camp we always offer the bonus pick for a bonus stage. On a RESUMED
  // camp we only offer it if this stage's slot isn't already filled — otherwise a
  // player who picked + advanced then abandoned mid-fight could grab a 2nd bonus.
  const expectedBonusCount = Math.min(MAX_ACTIVE_BONUSES, BONUS_STAGES.filter((s) => s <= clearedStage).length);
  const alreadyPickedThisCamp = activeBonuses.length >= expectedBonusCount;
  // Variant B: no boost draft at all — spells were locked at run start, the camp
  // is a pure rest stop (regen + enemy previews).
  const isBonusStageHere = !spellsVariant && isBonusStage(clearedStage)
    && (!resumed || (!alreadyPickedThisCamp && activeBonuses.length < MAX_ACTIVE_BONUSES));
  // Show the picker only until the boost is confirmed for this camp.
  const pickingBonus = isBonusStageHere && !confirmed;
  const needsReplace = activeBonuses.length >= MAX_ACTIVE_BONUSES;
  // Boosts are unique per run. Each camp offers a RANDOM draft (BOOST_DRAFT_SIZE)
  // drawn from the not-yet-owned pool — computed ONCE on mount (lazy useState) so
  // it stays stable across the per-second regen re-renders. A fresh camp = new mount
  // = new draft. Owned boosts can never reappear.
  const [offered] = useState<PveBoostId[]>(() =>
    sampleN(BOOST_POOL.filter((id) => !activeBonuses.includes(id)), BOOST_DRAFT_SIZE)
  );

  const [selected, setSelected] = useState<PveBoostId | null>(null);
  const [replaceIdx, setReplaceIdx] = useState<number | null>(null);
  const [infoBoost, setInfoBoost] = useState<PveBoostId | null>(null); // active-slot info popup
  const [infoSpell, setInfoSpell] = useState<PveSpellId | null>(null); // variant B: loadout info popup
  const [replaceOpen, setReplaceOpen] = useState(false);               // replace modal (slots full)

  const maxCond = maxConditionWithBonuses(activeBonuses);
  // Keep the decimal — the bar/regen recompute live; only the HP figure rounds (UI).
  const cond = Math.max(0, Math.min(maxCond, condition));
  const missing = Math.max(0, maxCond - cond);
  const msToFull = maxCond > 0 ? (missing / maxCond) * CONDITION_REGEN_MS : 0;
  const isFull = missing <= 0.0001;

  // Condition IS the player's HP: no continuing a run at 0 (recover first).
  const hasCondition = cond > 0;
  // Step-1 (confirm) is valid once a boost — and a replace target if slots are full — is picked.
  const bonusReady = selected !== null && (!needsReplace || replaceIdx !== null);
  // Step-2 (continue) needs HP only; the boost (if any) is already committed by then.
  const canContinue = hasCondition;

  const confirmBonus = () => {
    if (!bonusReady) return;
    onConfirmBonus(selected!, replaceIdx ?? undefined, offered);
    setConfirmed(true);
    setSelected(null);
    setReplaceIdx(null);
  };

  // Clicking a draft card: with a free slot → just select it (Confirm CTA below).
  // With slots full → open the replace modal (pick take + replace target there).
  const onCardClick = (b: PveBoostId) => {
    setSelected(b);
    if (needsReplace) { setReplaceIdx(null); setReplaceOpen(true); }
  };
  const replaceReady = selected !== null && replaceIdx !== null;
  const confirmReplace = () => {
    if (!replaceReady) return;
    onConfirmBonus(selected!, replaceIdx!, offered);
    setConfirmed(true);
    setReplaceOpen(false);
    setSelected(null);
    setReplaceIdx(null);
  };

  return (
    // Extra bottom padding so the fixed BottomNav never covers the CTA on scroll.
    <div style={{ position: "relative", padding: "18px 16px 108px", maxWidth: 480, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16, fontFamily: FONT.ui }}>
      <style>{ST_STYLE}</style>

      {/* Camp title — back button (left) + current tier label (right), both absolute
          on the title's level so the "Camp" heading stays centered. The tier label
          tells the player which tier this run is (otherwise invisible on this screen). */}
      <div style={{ position: "relative", textAlign: "center", animation: "stBanner 0.45s cubic-bezier(0.34,1.56,0.64,1) both" }}>
        <button onClick={onBack} title="Leave — your run stays active and keeps recovering" style={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", width: 38, height: 38, display: "grid", placeItems: "center", border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: C.text, borderRadius: 10, fontSize: 16, cursor: "pointer" }}>←</button>
        <div style={{ font: `700 24px ${FONT.display}`, letterSpacing: 2, textTransform: "uppercase", color: C.text }}>{st("camp")}</div>
        {(() => {
          const act = actForTier(tier);
          const name = getTier(tier)?.tierName;
          return (
            <div style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, maxWidth: "42%" }}>
              <span style={{ font: `800 10px ${FONT.label}`, letterSpacing: 1.5, color: act.color, background: `${act.color}1F`, border: `1px solid ${act.color}55`, borderRadius: 5, padding: "2px 7px", whiteSpace: "nowrap" }}>{st("tierN", tier)}</span>
              {name && <span style={{ font: `700 10px ${FONT.ui}`, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{name}</span>}
            </div>
          );
        })()}
      </div>

      {/* Condition + regen — same shared meter as the pre-run screen */}
      <ConditionMeter
        conditionPct={cond}
        condMax={maxCond}
        maxHp={maxHp}
        msToFull={msToFull}
        isFull={isFull}
      />

      {/* Combat stats — Glass Cannon previews its Defense → 0 trade-off on select. */}
      <StatPreview playerStats={playerStats} maxHp={maxHp} preview={pickingBonus ? selected : null} energyBonus={energyBonus} />

      {/* Active boosts (control) / locked spell loadout (variant B) */}
      <div style={{ background: "linear-gradient(180deg,#121a2b,#0c1322)", borderRadius: 16, padding: 15, border: `1px solid rgba(255,255,255,0.08)`, clipPath: ST_BEVEL }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 11, font: `800 11px ${FONT.label}`, letterSpacing: 2, color: C.muted }}>
          <span style={{ width: 5, height: 12, background: "#4CC9F0", borderRadius: 1, display: "inline-block" }} />{spellsVariant ? st("yourSpells") : st("activeBoosts")}
        </div>
        {spellsVariant ? (
          // Locked run spells — tap for effect + cost (read-only until run end).
          <div style={{ display: "flex", gap: 8 }}>
            {sortByCostDesc(spellLoadout).map((id) => {
              const col = spellColor(id);
              return (
                <button key={id} onClick={() => setInfoSpell(id)} title={spellName(id, lang)} style={{ position: "relative", width: 44, height: 44, borderRadius: 11, border: `1.5px solid ${col}66`, background: `${col}22`, boxShadow: `0 0 12px ${col}66`, display: "grid", placeItems: "center", fontSize: 20, cursor: "pointer" }}>
                  {spellIcon(id)}
                  <span style={{ position: "absolute", top: -6, right: -6, font: `800 9px ${FONT.display}`, color: "#0c1322", background: C.gold, borderRadius: 7, padding: "1px 4px" }}>{getSpell(id)?.cost ?? "?"}</span>
                </button>
              );
            })}
          </div>
        ) : (
          /* 3 slots (proto dims 44×44) — filled = glowing icon (tap for effects), empty = dashed. */
          <div style={{ display: "flex", gap: 8 }}>
            {Array.from({ length: MAX_ACTIVE_BONUSES }, (_, i) => {
              const b = activeBonuses[i];
              if (!b) {
                return <div key={i} style={{ width: 44, height: 44, borderRadius: 11, border: "1.5px dashed rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)" }} />;
              }
              const col = boostColor(b);
              return (
                <button key={i} onClick={() => setInfoBoost(b)} title={boostName(b)} style={{ width: 44, height: 44, borderRadius: 11, border: `1.5px solid ${col}66`, background: `${col}22`, boxShadow: `0 0 12px ${col}66`, display: "grid", placeItems: "center", fontSize: 20, cursor: "pointer" }}>{boostIcon(b)}</button>
              );
            })}
          </div>
        )}
      </div>

      {/* Bonus pick (only on bonus stages, until confirmed) */}
      {pickingBonus && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 7, font: `800 11px ${FONT.label}`, letterSpacing: 2, textTransform: "uppercase", color: C.muted }}>
              <span style={{ width: 5, height: 12, background: "#A855F7", borderRadius: 1, display: "inline-block" }} />{st("chooseBoost")}
            </span>
            {!needsReplace && (
              <span style={{ font: `800 10px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", color: "#A855F7", whiteSpace: "nowrap" }}>
                {st("slotN", activeBonuses.length + 1, MAX_ACTIVE_BONUSES)}
              </span>
            )}
          </div>
          {needsReplace && (
            <div style={{ font: `600 11px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: "#A855F7", marginTop: -6 }}>
              {st("slotsFull")}
            </div>
          )}

          {/* 3 boost cards side by side — proto dimensions/style (tinted to the boost color) */}
          <div style={{ display: "flex", gap: 9 }}>
            {offered.map((b) => {
              const on = selected === b && !needsReplace; // highlight only in the free-slot flow
              const col = boostColor(b);
              return (
                <button
                  key={b}
                  onClick={() => onCardClick(b)}
                  style={{
                    position: "relative", overflow: "hidden", flex: 1, minWidth: 0, textAlign: "left", cursor: "pointer",
                    borderRadius: 13, padding: "13px 11px",
                    background: `linear-gradient(180deg,${col}18,${col}05)`,
                    border: `1.5px solid ${on ? col : col + "55"}`,
                    boxShadow: on ? `0 0 22px ${col}66` : `0 0 16px ${col}22`,
                    transition: "border-color 0.15s, box-shadow 0.15s",
                  }}
                >
                  <div style={{ position: "absolute", top: 0, left: 0, width: "55%", height: "100%", background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.18),transparent)", animation: "stShine 3.6s ease-in-out infinite", pointerEvents: "none" }} />
                  <div style={{ position: "relative", width: 38, height: 38, display: "grid", placeItems: "center", borderRadius: 10, fontSize: 20, background: `${col}22`, border: `1px solid ${col}55`, boxShadow: `0 0 12px ${col}55` }}>{boostIcon(b)}</div>
                  <div style={{ position: "relative", font: `700 13px ${FONT.display}`, color: col, marginTop: 9 }}>{boostName(b, lang)}</div>
                  <div style={{ position: "relative", font: `600 10.5px ${FONT.ui}`, color: "#9fb0c8", lineHeight: 1.28, marginTop: 5 }}>{boostDesc(b, lang)}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* What's ahead — Next Minion (left) + Next Boss (right), tap to inspect.
          Only cards that still exist ahead are rendered; a lone card fills the row
          (e.g. before the final boss there is no minion left). */}
      <div style={{ display: "flex", gap: 10 }}>
        {/* Next Minion (left) */}
        {nextMinionStage && (
          <button
            onClick={() => setStatPopupStage(nextMinionStage)}
            style={{ flex: 1, minWidth: 0, textAlign: "left", cursor: "pointer", borderRadius: 13, padding: "12px 13px", background: "linear-gradient(180deg,#121a2b,#0c1322)", border: "1px solid rgba(255,255,255,0.10)" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
              <span style={{ font: `800 10px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted }}>{st("nextMinion")}</span>
              <span style={{ font: `800 9px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", padding: "2px 7px", borderRadius: 5, color: C.muted, background: "rgba(255,255,255,0.06)" }}>{`S${nextMinionStage}`}</span>
            </div>
            <div style={{ font: `700 15px ${FONT.display}`, color: C.text, marginTop: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{st("minion")}</div>
            <div style={{ font: `700 9.5px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: "#7FA9D8", marginTop: 7 }}>{st("checkStats")} →</div>
          </button>
        )}

        {/* Next Boss (right) */}
        {nextBossStage && (
          <button
            onClick={() => setStatPopupStage(nextBossStage)}
            style={{ flex: 1, minWidth: 0, textAlign: "left", cursor: "pointer", borderRadius: 13, padding: "12px 13px", background: "linear-gradient(180deg,#1c1220,#140c14)", border: "1px solid rgba(255,59,71,0.30)" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
              <span style={{ font: `800 10px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", color: "#FF6B6B" }}>{st("nextBoss")}</span>
              <span style={{ font: `800 9px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", padding: "2px 7px", borderRadius: 5, color: "#FF6B6B", background: "rgba(255,59,71,0.14)" }}>{bossIsFinal ? st("tagFinal") : `S${nextBossStage}`}</span>
            </div>
            <div style={{ font: `700 15px ${FONT.display}`, color: C.text, marginTop: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{bossName}</div>
            <div style={{ font: `700 9.5px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: "#FF8A90", marginTop: 7 }}>{st("checkStats")} →</div>
          </button>
        )}
      </div>

      {/* Enemy stats popup — opened by either card above */}
      {statPopupStage !== null && (
        <EnemyStatsModal tier={tier} stage={statPopupStage} onClose={() => setStatPopupStage(null)} />
      )}

      {!hasCondition && !pickingBonus && (
        <div style={{ textAlign: "center", font: `700 12px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: C.live }}>
          {st("recoverFirst")}
        </div>
      )}

      {pickingBonus && !needsReplace ? (
        // Step 1 — confirm the boost (adds it, then reveals the Continue button).
        <button
          onClick={confirmBonus}
          disabled={!bonusReady}
          style={{
            background: bonusReady ? "linear-gradient(135deg,#8A5CFF,#A855F7)" : "rgba(255,255,255,0.06)",
            border: "none", borderRadius: 14, padding: "16px 0",
            font: `800 17px ${FONT.display}`, letterSpacing: 1.5, textTransform: "uppercase",
            color: bonusReady ? "#fff" : C.muted, cursor: bonusReady ? "pointer" : "not-allowed",
            clipPath: ST_BEVEL,
            boxShadow: bonusReady ? "0 10px 26px rgba(168,85,247,0.4)" : "none",
          }}
        >
          {bonusReady ? st("confirmBoost") : st("selectBoost")}
        </button>
      ) : !pickingBonus ? (
        // Step 2 — launch the next fight with the current (regenerating) HP.
        <button
          onClick={() => { if (canContinue) onContinue(); }}
          disabled={!canContinue}
          style={{
            background: canContinue ? "linear-gradient(135deg,#FF7A2C,#FF2E44)" : "rgba(255,255,255,0.06)",
            border: "none", borderRadius: 14, padding: "16px 0",
            font: `800 17px ${FONT.display}`, letterSpacing: 1.5, textTransform: "uppercase",
            color: canContinue ? "#fff" : C.muted, cursor: canContinue ? "pointer" : "not-allowed",
            clipPath: ST_BEVEL,
            animation: canContinue ? "stCtaGlow 2.6s ease-in-out infinite" : undefined,
          }}
        >
          {canContinue ? st("startStage", nextStage) : st("noCondition")}
        </button>
      ) : null}

      {/* Active-boost info popup — tap a slot to read its effect */}
      {infoBoost && (
        <div onClick={() => setInfoBoost(null)} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 320, background: "linear-gradient(180deg,#141d31,#0c1424)", border: `1.5px solid ${boostColor(infoBoost)}`, borderRadius: 18, padding: 20, textAlign: "center", clipPath: ST_BEVEL, boxShadow: `0 0 30px ${boostColor(infoBoost)}44` }}>
            <div style={{ width: 60, height: 60, margin: "0 auto 12px", display: "grid", placeItems: "center", borderRadius: 14, background: boostBg(infoBoost), border: `1px solid ${boostColor(infoBoost)}55`, boxShadow: `inset 0 0 18px ${boostColor(infoBoost)}33`, fontSize: 34 }}>{boostIcon(infoBoost)}</div>
            <div style={{ font: `700 18px ${FONT.display}`, letterSpacing: 0.5, color: boostColor(infoBoost) }}>{boostName(infoBoost, lang)}</div>
            <div style={{ font: `500 13px ${FONT.ui}`, color: C.muted, marginTop: 8 }}>{boostDesc(infoBoost, lang)}</div>
            <button onClick={() => setInfoBoost(null)} style={{ marginTop: 16, width: "100%", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.05)", color: C.text, borderRadius: 10, padding: "10px 0", font: `700 12px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer" }}>{st("close")}</button>
          </div>
        </div>
      )}

      {/* Variant B — locked-spell info popup (name, cost, effect) */}
      {infoSpell && (
        <div onClick={() => setInfoSpell(null)} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 320, background: "linear-gradient(180deg,#141d31,#0c1424)", border: `1.5px solid ${spellColor(infoSpell)}`, borderRadius: 18, padding: 20, textAlign: "center", clipPath: ST_BEVEL, boxShadow: `0 0 30px ${spellColor(infoSpell)}44` }}>
            <div style={{ width: 60, height: 60, margin: "0 auto 12px", display: "grid", placeItems: "center", borderRadius: 14, background: spellBg(infoSpell), border: `1px solid ${spellColor(infoSpell)}55`, boxShadow: `inset 0 0 18px ${spellColor(infoSpell)}33`, fontSize: 34 }}>{spellIcon(infoSpell)}</div>
            <div style={{ font: `700 18px ${FONT.display}`, letterSpacing: 0.5, color: spellColor(infoSpell) }}>{spellName(infoSpell, lang)}</div>
            <div style={{ font: `800 11px ${FONT.label}`, letterSpacing: 1, color: C.gold, marginTop: 4 }}>{st("spellCost", (getSpell(infoSpell)?.cost ?? 0) * (PVE_ROTATION_ENABLED ? ROTATION_GAUGE_SCALE : 1))}</div>
            <div style={{ font: `500 13px ${FONT.ui}`, color: C.muted, marginTop: 8 }}>{spellDesc(infoSpell, lang)}</div>
            {(() => {
              const chips = spellValueChips(infoSpell, playerStats.physique, maxHp, playerLevel, st);
              return chips.length > 0 ? (
                <div style={{ font: `800 12px ${FONT.display}`, color: C.gold, marginTop: 8 }}>{chips.join(" · ")}</div>
              ) : null;
            })()}
            <button onClick={() => setInfoSpell(null)} style={{ marginTop: 16, width: "100%", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.05)", color: C.text, borderRadius: 10, padding: "10px 0", font: `700 12px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer" }}>{st("close")}</button>
          </div>
        </div>
      )}

      {/* Replace modal — 3 slots full: pick a new boost (top) + which to replace (bottom) */}
      {replaceOpen && (
        <div onClick={() => setReplaceOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 480, maxHeight: "88vh", overflowY: "auto", background: "linear-gradient(180deg,#141d31,#0b1120)", borderTopLeftRadius: 22, borderTopRightRadius: 22, borderTop: "1px solid rgba(255,255,255,0.1)", padding: "18px 16px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ font: `700 18px ${FONT.display}`, letterSpacing: 1, textTransform: "uppercase", color: C.text, textAlign: "center" }}>{st("replaceTitle")}</div>

            {/* New boost (take) */}
            <div style={{ display: "flex", alignItems: "center", gap: 7, font: `800 10px ${FONT.label}`, letterSpacing: 2, color: "#4CC9F0" }}>
              <span style={{ width: 5, height: 12, background: "#4CC9F0", borderRadius: 1, display: "inline-block" }} />{st("newBoost")}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {offered.map((b) => {
                const on = selected === b;
                return (
                  <button key={b} onClick={() => setSelected(b)} style={{ flex: 1, minWidth: 0, background: on ? boostBg(b) : "rgba(255,255,255,0.03)", border: `1.5px solid ${on ? boostColor(b) : "rgba(255,255,255,0.09)"}`, boxShadow: on ? `0 0 16px ${boostColor(b)}44` : "none", borderRadius: 12, padding: "10px 8px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 38, height: 38, display: "grid", placeItems: "center", borderRadius: 9, background: "rgba(0,0,0,0.28)", border: `1px solid ${boostColor(b)}44`, fontSize: 21 }}>{boostIcon(b)}</span>
                    <span style={{ font: `700 11px ${FONT.display}`, color: on ? boostColor(b) : C.text, textAlign: "center" }}>{boostName(b, lang)}</span>
                  </button>
                );
              })}
            </div>
            {/* Selected new-boost description */}
            {selected && (
              <div style={{ background: `${boostColor(selected)}14`, border: `1px solid ${boostColor(selected)}33`, borderRadius: 10, padding: "9px 11px" }}>
                <span style={{ font: `700 11px ${FONT.display}`, color: boostColor(selected) }}>{boostName(selected, lang)} · </span>
                <span style={{ font: `500 11.5px ${FONT.ui}`, color: C.muted }}>{boostDesc(selected, lang)}</span>
              </div>
            )}

            {/* Replace which */}
            <div style={{ display: "flex", alignItems: "center", gap: 7, font: `800 10px ${FONT.label}`, letterSpacing: 2, color: C.live, marginTop: 2 }}>
              <span style={{ width: 5, height: 12, background: C.live, borderRadius: 1, display: "inline-block" }} />{st("replaceWhich")}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {activeBonuses.map((b, i) => {
                const on = replaceIdx === i;
                return (
                  <button key={i} onClick={() => setReplaceIdx(i)} style={{ flex: 1, minWidth: 0, background: on ? "rgba(255,59,71,0.12)" : "rgba(255,255,255,0.03)", border: `1.5px solid ${on ? "rgba(255,59,71,0.6)" : "rgba(255,255,255,0.09)"}`, borderRadius: 12, padding: "10px 8px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 38, height: 38, display: "grid", placeItems: "center", borderRadius: 9, background: "rgba(0,0,0,0.28)", border: `1px solid ${on ? "rgba(255,59,71,0.5)" : boostColor(b) + "44"}`, fontSize: 21 }}>{boostIcon(b)}</span>
                    <span style={{ font: `700 11px ${FONT.display}`, color: on ? C.live : C.text, textAlign: "center" }}>{boostName(b, lang)}</span>
                  </button>
                );
              })}
            </div>
            {/* Selected replace-target description */}
            {replaceIdx !== null && activeBonuses[replaceIdx] && (
              <div style={{ background: "rgba(255,59,71,0.08)", border: "1px solid rgba(255,59,71,0.25)", borderRadius: 10, padding: "9px 11px" }}>
                <span style={{ font: `700 11px ${FONT.display}`, color: C.live }}>{boostName(activeBonuses[replaceIdx], lang)} · </span>
                <span style={{ font: `500 11.5px ${FONT.ui}`, color: C.muted }}>{boostDesc(activeBonuses[replaceIdx], lang)}</span>
              </div>
            )}

            <button onClick={confirmReplace} disabled={!replaceReady} style={{ marginTop: 4, background: replaceReady ? "linear-gradient(135deg,#FF7A2C,#FF2E44)" : "rgba(255,255,255,0.06)", border: "none", borderRadius: 14, padding: "15px 0", font: `800 16px ${FONT.display}`, letterSpacing: 1.5, textTransform: "uppercase", color: replaceReady ? "#fff" : C.muted, cursor: replaceReady ? "pointer" : "not-allowed", clipPath: ST_BEVEL }}>
              {st("replaceBoost")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Player combat stats card. New boosts mostly act during combat (shield, execute,
// heal, last stand) rather than on a base stat — the picker card describes them.
// The one stat-changing boost is Glass Cannon (Defense → 0): when it is previewed,
// the Defense row reflects that so the trade-off is visible before confirming.
function StatPreview({ playerStats, maxHp, preview, energyBonus = 0 }: {
  playerStats: EquipmentStats;
  maxHp: number;
  preview: PveBoostId | null;
  energyBonus?: number;
}) {
  const { st } = useST();
  const glassPreview = preview === "glass_cannon";
  const curDef = Math.round(playerStats.defense);
  // Vie (HP) toujours en premier, puis les 3 autres, puis l'énergie (peut monter).
  const rows: { label: string; icon: string; value: string; hint: string; changed?: boolean; changedTo?: string }[] = [
    { label: st("statLife"),      icon: "❤️", value: `${Math.round(maxHp)}`,                 hint: st("hintMaxHp") },
    { label: st("statPower"),     icon: "💪", value: `${Math.round(playerStats.physique)}`,  hint: glassPreview ? st("hintDmgUp") : st("hintDmgHit"), changed: glassPreview },
    { label: st("statTechnique"), icon: "🎯", value: `${Math.round(playerStats.technique)}`, hint: st("hintCrit") },
    { label: st("statDefense"),   icon: "🛡️", value: `${curDef}`, hint: st("hintTaken", Math.round(100 * defenseReductionFor(curDef))), changed: glassPreview, changedTo: "0" },
    { label: st("energyLabel"),   icon: "💧", value: `${ENERGY_PER_TURN + energyBonus}`,     hint: st("perTurn") },
  ];
  const accent = getBoost("glass_cannon")?.color ?? C.live;
  return (
    <div style={{ background: "linear-gradient(180deg,#121a2b,#0c1322)", borderRadius: 16, padding: 15, border: `1px solid rgba(255,255,255,0.08)`, display: "flex", flexDirection: "column", gap: 9, clipPath: ST_BEVEL, fontFamily: FONT.ui }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, font: `800 11px ${FONT.label}`, letterSpacing: 2, color: C.muted }}>
        <span style={{ width: 5, height: 12, background: "#4CC9F0", borderRadius: 1, display: "inline-block" }} />{st("yourStats")}
      </div>
      {rows.map((r) => (
        <div key={r.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ font: `700 13px ${FONT.ui}`, color: r.changed ? accent : C.text }}>
            {r.icon} {r.label}
            <span style={{ font: `600 10px ${FONT.ui}`, color: r.changed ? accent : C.muted, marginLeft: 6 }}>{r.hint}</span>
          </span>
          {r.changed && r.changedTo != null ? (
            <span style={{ font: `700 14px ${FONT.display}`, color: accent }}>
              {r.changedTo} <span style={{ fontSize: 11, color: C.muted, textDecoration: "line-through" }}>{r.value}</span>
            </span>
          ) : (
            <span style={{ font: `700 14px ${FONT.display}`, color: C.text }}>{r.value}</span>
          )}
        </div>
      ))}
    </div>
  );
}

// Pre-fight "Check Stats" popup for an upcoming enemy (minion or boss). Reads the
// SAME stat block the fight uses (getEnemyStatPreview) so what you see is what you
// fight. Boss effects are listed with their name/description.
function EnemyStatsModal({ tier, stage, onClose }: {
  tier: number;
  stage: number;
  onClose: () => void;
}) {
  const { st, lang } = useST();
  const enc = getEncounter(tier, stage);
  const preview: EnemyStatPreview | undefined = getEnemyStatPreview(tier, stage);
  const isBoss = enc?.kind === "boss";
  const isFinal = isBoss && stage === TOTAL_STAGES;
  const name = isBoss ? (enc?.name ?? getEnemy(enc?.enemyId ?? "")?.name ?? "Boss") : st("minion");
  const tag = isFinal ? st("tagFinal") : isBoss ? st("tagBoss") : st("tagMob");
  const effects = (enc?.effects ?? []).map(getBossEffect).filter((e): e is NonNullable<typeof e> => !!e);
  const accent = isBoss ? "#FF6B6B" : "#4CC9F0";

  if (!preview) return null;
  const def = preview.defense;
  const rows: { icon: string; label: string; value: string; hint?: string }[] = [
    { icon: "💪", label: st("statPower"),     value: `${preview.physique}`,  hint: st("hintDmgHit") },
    { icon: "🎯", label: st("statTechnique"), value: `${preview.technique}`, hint: st("hintCrit") },
    { icon: "❤️", label: st("statHpMax"),     value: `${preview.maxHp}` },
    { icon: "🛡️", label: st("statDefense"),   value: `${def}`, hint: st("hintTaken", Math.round(100 * defenseReductionFor(def))) },
  ];
  if (preview.damageMultiplier !== 1) {
    rows.push({ icon: "⚔️", label: st("statDamage"), value: `×${preview.damageMultiplier}`, hint: st("hintDmgOut") });
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.68)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 340, background: "linear-gradient(180deg,#141d31,#0c1424)", border: `1.5px solid ${accent}`, borderRadius: 18, padding: 20, clipPath: ST_BEVEL, boxShadow: `0 0 30px ${accent}44`, fontFamily: FONT.ui }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span style={{ font: `800 10px ${FONT.label}`, letterSpacing: 2, textTransform: "uppercase", color: C.muted }}>{st("enemyStatsTitle")}</span>
          <span style={{ font: `800 9px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", padding: "2px 8px", borderRadius: 5, color: accent, background: `${accent}22` }}>{tag} · S{stage}</span>
        </div>
        <div style={{ font: `700 20px ${FONT.display}`, color: C.text, marginTop: 6, marginBottom: 12 }}>{name}</div>

        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {rows.map((r) => (
            <div key={r.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ font: `700 13px ${FONT.ui}`, color: C.text }}>
                {r.icon} {r.label}
                {r.hint && <span style={{ font: `600 10px ${FONT.ui}`, color: C.muted, marginLeft: 6 }}>{r.hint}</span>}
              </span>
              <span style={{ font: `700 14px ${FONT.display}`, color: C.text }}>{r.value}</span>
            </div>
          ))}
        </div>

        {effects.map((e) => (
          <div key={e.id} style={{ display: "flex", gap: 7, alignItems: "flex-start", marginTop: 11, padding: "9px 10px", borderRadius: 10, background: "rgba(255,59,71,0.09)", border: "1px solid rgba(255,59,71,0.24)" }}>
            <span style={{ fontSize: 14 }}>{e.icon || "⚠️"}</span>
            <div>
              <div style={{ font: `700 12px ${FONT.display}`, color: "#FFAEB3" }}>{lang === "ko" && e.nameKo ? e.nameKo : lang === "fr" && e.nameFr ? e.nameFr : e.name}</div>
              <div style={{ font: `600 11px ${FONT.ui}`, color: "#FFAEB3", lineHeight: 1.3, opacity: 0.85 }}>{lang === "ko" && e.descKo ? e.descKo : lang === "fr" && e.descFr ? e.descFr : e.description}</div>
            </div>
          </div>
        ))}

        <button onClick={onClose} style={{ marginTop: 16, width: "100%", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.05)", color: C.text, borderRadius: 10, padding: "10px 0", font: `700 12px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer" }}>{st("close")}</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function RunResultScreen({ phase, bestStage, onExit, onRetry }: {
  phase: Extract<Phase, { kind: "run-result" }>;
  bestStage: number;
  onExit: () => void;
  onRetry?: () => void;
}) {
  const { reachedStage, cleared, totalXp, totalGold, chestEarned, error, retryable, saving, saved } = phase;
  const newBest = reachedStage > bestStage;

  return (
    <div style={{ padding: "22px 16px 26px", maxWidth: 480, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16, fontFamily: FONT.ui }}>
      <style>{ST_STYLE}</style>
      <style>{`@keyframes stSpin { to { transform: rotate(360deg); } }`}</style>

      {/* Banner */}
      <div style={{
        background: cleared ? "linear-gradient(180deg, rgba(245,179,1,0.10), rgba(12,19,34,0.4))" : "linear-gradient(180deg,#121a2b,#0c1322)",
        border: `1.5px solid ${cleared ? "rgba(245,179,1,0.3)" : "rgba(255,255,255,0.08)"}`,
        borderRadius: 20, padding: "26px 20px", textAlign: "center", display: "flex", flexDirection: "column", gap: 10,
        clipPath: ST_BEVEL, animation: "stBanner 0.45s cubic-bezier(0.34,1.56,0.64,1) both",
        boxShadow: cleared ? "0 0 30px rgba(245,179,1,0.14)" : "0 6px 18px rgba(0,0,0,0.3)",
      }}>
        <div style={{ fontSize: 56 }}>{cleared ? "🏆" : "📊"}</div>
        <div style={{ font: `700 26px ${FONT.display}`, letterSpacing: 1.5, textTransform: "uppercase", color: cleared ? C.gold : C.text, textShadow: cleared ? "0 0 22px rgba(245,179,1,0.5)" : "none" }}>
          {cleared ? "Stadium Cleared!" : `Reached Stage ${reachedStage}`}
        </div>
        {newBest && <div style={{ font: `800 11px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", color: C.turf }}>★ New personal best!</div>}
      </div>

      {/* Rewards earned */}
      {!error && (totalXp > 0 || totalGold > 0 || chestEarned) && (
        <div style={{ background: "linear-gradient(180deg,#121a2b,#0c1322)", borderRadius: 16, padding: 16, border: "1px solid rgba(255,255,255,0.08)", clipPath: ST_BEVEL }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14, font: `800 11px ${FONT.label}`, letterSpacing: 2, color: C.muted }}>
            <span style={{ width: 5, height: 12, background: C.gold, borderRadius: 1, display: "inline-block" }} />REWARDS EARNED
          </div>
          <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
            {totalXp > 0 && <RewardBadge icon="⚡" label="XP" value={`+${totalXp}`} big />}
            {totalGold > 0 && <RewardBadge icon={<GoldIcon big />} label="Gold" value={`+${totalGold}`} big />}
            {chestEarned && <RewardBadge icon="📦" label="Chest" value="Opened!" big />}
          </div>
        </div>
      )}

      {saving && !error && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: "linear-gradient(180deg,#121a2b,#0c1322)", borderRadius: 14, padding: "12px 16px", border: "1px solid rgba(255,255,255,0.08)" }}>
          <span style={{ display: "inline-block", width: 15, height: 15, border: "2px solid rgba(255,255,255,0.25)", borderTopColor: "#fff", borderRadius: "50%", animation: "stSpin 0.7s linear infinite" }} />
          <span style={{ font: `600 12px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: C.text }}>Saving rewards…</span>
        </div>
      )}

      {saved && !error && (
        <div style={{ textAlign: "center", font: `800 12px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: C.turf, background: "rgba(45,227,107,0.08)", border: "1px solid rgba(45,227,107,0.25)", borderRadius: 14, padding: "12px 16px" }}>
          ✓ Rewards saved
        </div>
      )}

      {error && (
        <div style={{ background: "rgba(255,59,71,0.08)", border: "1px solid rgba(255,59,71,0.25)", borderRadius: 14, padding: 16, font: `500 13px ${FONT.ui}`, color: C.live }}>
          {error}
        </div>
      )}

      {/* Retry only for transient failures */}
      {error && retryable && (
        <button
          onClick={onRetry}
          style={{
            background: "linear-gradient(135deg,#FF7A2C,#FF2E44)",
            border: "none", borderRadius: 14, padding: "16px 0",
            font: `800 17px ${FONT.display}`, letterSpacing: 1.5, textTransform: "uppercase",
            color: "#fff", cursor: "pointer", clipPath: ST_BEVEL,
          }}
        >
          ↻ Retry save
        </button>
      )}

      <button
        onClick={saving ? undefined : onExit}
        disabled={saving}
        style={{
          background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 14, padding: "15px 0",
          font: `700 14px ${FONT.display}`, letterSpacing: 1, textTransform: "uppercase",
          color: saving ? C.muted : C.text, cursor: saving ? "not-allowed" : "pointer",
        }}
      >
        ← Back to League Hub
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared tiny components
// ─────────────────────────────────────────────────────────────────────────────

// Game-style loot tile: colored square with a contour + radial glow, icon, amount.
// Inspired by the reward-notification screen. Purely presentational.
function RewardTile({ icon, amount, label, contour, glow, delay }: {
  icon: React.ReactNode; amount: string; label: string; contour: string; glow: string; delay: number;
}) {
  return (
    <div style={{ flex: 1, minWidth: 0, maxWidth: 108, display: "flex", flexDirection: "column", alignItems: "center", gap: 5, animation: `stLootPop 0.34s ${delay}ms cubic-bezier(0.34,1.56,0.64,1) both` }}>
      <div style={{ position: "relative", width: "100%" }}>
        <div style={{ position: "absolute", inset: -7, borderRadius: 16, background: `radial-gradient(ellipse at center, ${glow} 0%, transparent 70%)`, opacity: 0.30 }} />
        <div style={{ position: "relative", aspectRatio: "1 / 1", borderRadius: 13, border: `1.5px solid ${contour}`, background: "rgba(255,255,255,0.03)", overflow: "hidden", display: "grid", placeItems: "center", boxShadow: `inset 0 0 18px ${glow}22` }}>
          {/* shine sweep across the tile */}
          <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "55%", background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.16),transparent)", animation: `stShine 5s ${delay}ms ease-in-out infinite`, pointerEvents: "none" }} />
          <span style={{ position: "relative", fontSize: 26, lineHeight: 1, display: "grid", placeItems: "center", width: "100%", height: "100%", padding: 4, boxSizing: "border-box" }}>{icon}</span>
        </div>
      </div>
      <div style={{ font: `700 15px ${FONT.display}`, color: "#fff" }}>{amount}</div>
      <div style={{ font: `800 10px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: C.muted }}>{label}</div>
    </div>
  );
}

function RewardBadge({ icon, label, value, big }: { icon: React.ReactNode; label: string; value: string; big?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, flex: 1, maxWidth: 108 }}>
      <div style={{ width: big ? 54 : 44, height: big ? 54 : 44, display: "grid", placeItems: "center", borderRadius: 12, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", fontSize: big ? 26 : 20, lineHeight: 1 }}>{icon}</div>
      <span style={{ font: `700 ${big ? 18 : 14}px ${FONT.display}`, color: "#fff" }}>{value}</span>
      <span style={{ font: `800 10px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: C.muted }}>{label}</span>
    </div>
  );
}

// Roguelike run path — horizontal rail of 10 nodes (mobs S{n}, boss ☠, final 👑),
// current stage highlighted gold + pulse. Purely presentational: reads the tier's
// encounters (kind) and the current stage; no logic/state.
function RunPath({ tier, currentStage }: { tier: number; currentStage: number }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 7, margin: "0 2px 9px" }}>
        <span style={{ width: 5, height: 12, background: "#4CC9F0", borderRadius: 1, display: "inline-block" }} />
        <span style={{ font: `800 11px ${FONT.label}`, letterSpacing: 2, textTransform: "uppercase", color: C.muted }}>Run Path · 10 Stages</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 2, overflowX: "auto", padding: "8px 6px 12px", background: "linear-gradient(180deg,#0c1424,#0a101c)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14 }}>
        {Array.from({ length: TOTAL_STAGES }, (_, i) => i + 1).map((n) => {
          const enc = getEncounter(tier, n);
          const isBoss = enc?.kind === "boss";
          const isFinal = n === TOTAL_STAGES && isBoss;
          const isCurrent = n === currentStage;
          const size = isBoss ? 44 : 36;
          const icon = isFinal ? "👑" : isBoss ? "☠" : String(n);
          const numLabel = isFinal ? "FINAL" : isBoss ? "BOSS" : `S${n}`;
          const bg = isCurrent
            ? "radial-gradient(circle at 50% 30%, #2a3654, #141d31)"
            : isBoss ? "radial-gradient(circle at 50% 30%, #2a1420, #160b12)" : "linear-gradient(180deg,#141d31,#0d1626)";
          const border = isCurrent ? "2px solid #F5B301" : isBoss ? "1.5px solid rgba(255,59,71,0.4)" : "1px solid rgba(255,255,255,0.1)";
          const color = isCurrent ? "#F5B301" : isBoss ? "#FF6B6B" : "#5C6B86";
          const numColor = isBoss ? "#FF6B6B" : isCurrent ? "#F5B301" : "#5C6B86";
          return (
            <div key={n} style={{ display: "flex", alignItems: "center", gap: 2, flex: "none" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, width: 46 }}>
                <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: isBoss ? 12 : 11, background: bg, border, color, font: `700 ${isBoss ? 17 : 14}px ${FONT.display}`, animation: isCurrent ? "stNode 1.8s ease-out infinite" : undefined }}>{icon}</div>
                <span style={{ font: `800 8px ${FONT.label}`, letterSpacing: 0.5, color: numColor }}>{numLabel}</span>
              </div>
              {n !== TOTAL_STAGES && <div style={{ width: 12, height: 2, background: "linear-gradient(90deg,rgba(255,255,255,0.16),rgba(255,255,255,0.06))" }} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StageBar({ current, total, won }: { current: number; total: number; won?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {Array.from({ length: total }, (_, i) => {
        const stageNum = i + 1;
        const isPast = stageNum < current;
        const isCurrent = stageNum === current;
        return (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: isPast ? C.turf : isCurrent ? (won === false ? C.live : C.gold) : "rgba(255,255,255,0.1)",
            transition: "background 0.3s",
          }} />
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Color helpers
// ─────────────────────────────────────────────────────────────────────────────

function conditionColor(v: number): string {
  if (v > 66) return C.turf;
  if (v > 33) return C.gold;
  return C.live;
}

// Boost display helpers — all read from the catalogue (getBoost). Unknown/legacy
// ids fall back to neutral values so nothing crashes.
const boostBg = (b: string): string => getBoost(b)?.bg ?? "rgba(255,255,255,0.06)";
const boostColor = (b: string): string => getBoost(b)?.color ?? C.text;
const boostIcon = (b: string): string => getBoost(b)?.icon ?? "•";
const boostName = (b: string, lang = "en"): string => { const d = getBoost(b); if (!d) return b; if (lang === "ko" && d.nameKo) return d.nameKo; if (lang === "fr" && d.nameFr) return d.nameFr; return d.name; };
const boostDesc = (b: string, lang = "en"): string => { const d = getBoost(b); if (!d) return ""; if (lang === "ko" && d.descKo) return d.descKo; if (lang === "fr" && d.descFr) return d.descFr; return d.description; };

// Spell display helpers (A/B variant B) — same pattern, read from getSpell.
const spellBg = (s: string): string => getSpell(s)?.bg ?? "rgba(255,255,255,0.06)";
export const spellColor = (s: string): string => getSpell(s)?.color ?? getPetSpell(s)?.color ?? C.text;
export const spellIcon = (s: string): string => getSpell(s)?.icon ?? getPetSpell(s)?.icon ?? "•";
export const spellName = (s: string, lang = "en"): string => { const d = getSpell(s) ?? getPetSpell(s); if (!d) return s; if (lang === "ko" && d.nameKo) return d.nameKo; if (lang === "fr" && d.nameFr) return d.nameFr; return d.name; };
const spellDesc = (s: string, lang = "en"): string => { const d = getSpell(s) ?? getPetSpell(s); if (!d) return ""; if (lang === "ko" && d.descKo) return d.descKo; if (lang === "fr" && d.descFr) return d.descFr; return d.description; };

// Display order of a loadout: energy cost DESCENDING left→right (the cheapest
// spell sits on the RIGHT, thumb-side on mobile).
const sortByCostDesc = (ids: PveSpellId[]): PveSpellId[] =>
  [...ids].sort((a, b) => (getSpell(b)?.cost ?? 0) - (getSpell(a)?.cost ?? 0));

// The concrete numbers behind a spell's multipliers, localized: "80% attack
// damage" shows "≈68 dmg" for THIS player's Puissance, Wall shows its real
// level × 15 shield, etc. Display-only (the engine recomputes at cast time).
function spellValueChips(
  id: string,
  physique: number,
  maxHp: number,
  playerLevel: number,
  st: (key: any, ...args: any[]) => string
): string[] {
  const v = spellComputedValues(id, physique, maxHp, playerLevel);
  const chips: string[] = [];
  if (v.damage != null) chips.push(st("hintSpellDmg", v.damage));
  if (v.damagePerEnergy != null) chips.push(st("hintSpellDmgPerEnergy", v.damagePerEnergy));
  if (v.selfDamage != null) chips.push(st("hintSpellSelfDmg", v.selfDamage));
  if (v.healOnCrit != null) chips.push(st("hintSpellHealCrit", v.healOnCrit));
  if (v.shield != null) chips.push(st("hintSpellShield", v.shield));
  return chips;
}

// ─────────────────────────────────────────────────────────────────────────────
// A/B variant B — spell-select screen (run start)
// ─────────────────────────────────────────────────────────────────────────────

// Pick the run's spells: the whole catalogue is shown, level-locked entries are
// greyed with their unlock level. The pick is exactly SPELL_LOADOUT_SIZE (or
// every unlocked spell when a low-level player has fewer than that).
function SpellSelectScreen({ playerLevel, playerStats, onConfirm, onBack }: {
  playerLevel: number;
  playerStats: EquipmentStats; // to show the CONCRETE numbers behind each multiplier
  onConfirm: (loadout: PveSpellId[]) => void;
  onBack: () => void;
}) {
  const { st, lang } = useST();
  const maxHp = maxHpFromStats(playerStats.vitesse);
  const unlocked = spellsUnlockedAt(playerLevel);
  const required = Math.min(SPELL_LOADOUT_SIZE, Math.max(1, unlocked.length));
  // Mandatory spells (Classic Strike) are pre-selected and cannot be removed —
  // the player really picks the remaining required − mandatory slots.
  const [picked, setPicked] = useState<PveSpellId[]>(() => MANDATORY_SPELLS.filter((id) => unlocked.includes(id)));
  const ready = picked.length === required;

  const toggle = (id: PveSpellId) => {
    if (!unlocked.includes(id)) return;
    if (PVE_SPELLS[id].mandatory) return; // locked in, no removal
    setPicked((prev) => prev.includes(id)
      ? prev.filter((p) => p !== id)
      : prev.length < required ? [...prev, id] : prev);
  };

  return (
    <div style={{ padding: "18px 16px 26px", maxWidth: 480, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14, fontFamily: FONT.ui }}>
      <style>{ST_STYLE}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onBack} style={{ flex: "none", width: 38, height: 38, display: "grid", placeItems: "center", border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: C.text, borderRadius: 10, fontSize: 16, cursor: "pointer" }}>←</button>
        <div style={{ minWidth: 0 }}>
          <div style={{ font: `700 22px ${FONT.display}`, letterSpacing: 1, textTransform: "uppercase", color: C.text }}>{st("chooseSpells")}</div>
          <div style={{ font: `600 11px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: C.muted, marginTop: 3 }}>{st("spellsForRun", required)}</div>
        </div>
      </div>

      {/* Counter */}
      <div style={{ textAlign: "center", font: `800 12px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", color: ready ? C.turf : C.gold }}>
        {st("spellsSelected", picked.length, required)}
      </div>

      {/* Catalogue — 2-column grid, locked entries greyed with unlock level */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
        {SPELL_POOL.map((id) => {
          const def = PVE_SPELLS[id];
          const isUnlocked = unlocked.includes(id);
          const isMandatory = !!def.mandatory;
          const on = picked.includes(id);
          const col = def.color;
          return (
            <button
              key={id}
              onClick={() => toggle(id)}
              disabled={!isUnlocked}
              style={{
                position: "relative", overflow: "hidden", textAlign: "left",
                borderRadius: 13, padding: "12px 11px",
                background: isUnlocked ? `linear-gradient(180deg,${col}18,${col}05)` : "rgba(255,255,255,0.02)",
                border: `1.5px solid ${on ? col : isUnlocked ? col + "44" : "rgba(255,255,255,0.08)"}`,
                boxShadow: on ? `0 0 22px ${col}66` : "none",
                opacity: isUnlocked ? 1 : 0.45,
                cursor: isMandatory ? "default" : isUnlocked ? "pointer" : "not-allowed",
                transition: "border-color 0.15s, box-shadow 0.15s",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                <span style={{ width: 36, height: 36, display: "grid", placeItems: "center", borderRadius: 10, fontSize: 19, background: `${col}22`, border: `1px solid ${col}55` }}>{def.icon}</span>
                <span style={{ font: `800 11px ${FONT.display}`, color: C.gold, background: "rgba(245,179,1,0.13)", border: "1px solid rgba(245,179,1,0.3)", borderRadius: 5, padding: "2px 6px", whiteSpace: "nowrap" }}>{st("spellCost", PVE_ROTATION_ENABLED ? def.cost * ROTATION_GAUGE_SCALE : def.cost)}</span>
              </div>
              <div style={{ font: `700 13px ${FONT.display}`, color: isUnlocked ? col : C.muted, marginTop: 8 }}>{spellName(id, lang)}</div>
              <div style={{ font: `600 10.5px ${FONT.ui}`, color: "#9fb0c8", lineHeight: 1.28, marginTop: 4, minHeight: 27 }}>{spellDesc(id, lang)}</div>
              {(() => {
                const chips = spellValueChips(id, playerStats.physique, maxHp, playerLevel, st);
                return chips.length > 0 ? (
                  <div style={{ font: `800 10px ${FONT.display}`, color: isUnlocked ? C.gold : C.muted, marginTop: 5 }}>{chips.join(" · ")}</div>
                ) : null;
              })()}
              {!isUnlocked && (
                <div style={{ font: `800 9.5px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: C.muted, marginTop: 6 }}>🔒 {st("unlockAtLv", def.unlockLevel)}</div>
              )}
              {isMandatory && (
                <div style={{ font: `800 9.5px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: C.gold, marginTop: 6 }}>🔒 {st("alwaysEquipped")}</div>
              )}
              {on && (
                <div style={{ position: "absolute", top: 8, right: 8, width: 18, height: 18, borderRadius: "50%", background: col, color: "#0c1322", display: "grid", placeItems: "center", font: `800 11px ${FONT.display}` }}>✓</div>
              )}
            </button>
          );
        })}
      </div>

      {/* Confirm — locks the loadout for the WHOLE run */}
      <button
        onClick={ready ? () => onConfirm(picked) : undefined}
        disabled={!ready}
        style={{
          background: ready ? "linear-gradient(135deg,#8A5CFF,#A855F7)" : "rgba(255,255,255,0.06)",
          border: "none", borderRadius: 14, padding: "16px 0",
          font: `800 17px ${FONT.display}`, letterSpacing: 1.5, textTransform: "uppercase",
          color: ready ? "#fff" : C.muted, cursor: ready ? "pointer" : "not-allowed",
          clipPath: ST_BEVEL,
          boxShadow: ready ? "0 10px 26px rgba(168,85,247,0.4)" : "none",
        }}
      >
        {ready ? st("confirmSpells") : st("selectMoreSpells", required - picked.length)}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// A/B variant B — interactive turn-based combat screen
// ─────────────────────────────────────────────────────────────────────────────

// Mana gauge — pips filled up to the current mana. Water-blue for the player,
// red for the enemy's VISIBLE gauge (its spell is telegraphed by it).
function EnergyPips({ energy, max = ENERGY_MAX, enemy = false }: { energy: number; max?: number; enemy?: boolean }) {
  const size = enemy ? 12 : 15;
  return (
    <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
      {Array.from({ length: max }, (_, i) => {
        const filled = i < energy;
        return (
          <span key={i} style={{
            width: size, height: size, borderRadius: 4, transform: "rotate(45deg)",
            background: filled
              ? (enemy ? "linear-gradient(135deg,#FF8A90,#FF3B47)" : "linear-gradient(135deg,#7FE0FF,#4CC9F0)")
              : "rgba(255,255,255,0.07)",
            border: `1px solid ${filled ? (enemy ? "#FF3B47" : "#4CC9F0") : "rgba(255,255,255,0.14)"}`,
            boxShadow: filled ? (enemy ? "0 0 8px rgba(255,59,71,0.55)" : "0 0 8px rgba(76,201,240,0.55)") : "none",
            transition: "background 0.2s, box-shadow 0.2s",
          }} />
        );
      })}
    </div>
  );
}

// Combat FX keyframes — shared by the Stadium fight screen AND the Duel
// rotation fight (same animations, same layout — the Duel is a visual clone).
export const ST_COMBAT_KEYFRAMES = `
  @keyframes stKnockL0 { 0%,100% { transform: translateX(0); } 35% { transform: translateX(-8px); } }
  @keyframes stKnockL1 { 0%,100% { transform: translateX(0); } 35% { transform: translateX(-8px); } }
  @keyframes stKnockR0 { 0%,100% { transform: translateX(0); } 35% { transform: translateX(8px); } }
  @keyframes stKnockR1 { 0%,100% { transform: translateX(0); } 35% { transform: translateX(8px); } }
  @keyframes stDmgPop { 0% { opacity: 0; transform: translateY(8px); } 12% { opacity: 1; } 78% { opacity: 1; transform: translateY(-6px); } 100% { opacity: 0; transform: translateY(-16px); } }
  @keyframes stLungeR { 0%,100% { transform: translateX(0); } 40% { transform: translateX(18px); } }
  @keyframes stLungeL { 0%,100% { transform: translateX(0); } 40% { transform: translateX(-18px); } }
  @keyframes stProjFlyR { from { transform: translateX(-55px); opacity: 0.9; } 20% { opacity: 1; } 75% { opacity: 1; } to { transform: translateX(55px); opacity: 0; } }
  @keyframes stProjFlyL { from { transform: translateX(55px); opacity: 0.9; } 20% { opacity: 1; } 75% { opacity: 1; } to { transform: translateX(-55px); opacity: 0; } }
  @keyframes stEndPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(45,227,107,0.5); } 50% { box-shadow: 0 0 0 8px rgba(45,227,107,0); } }
`;

// Rotation gauge — a REAL filling bar (0..ROTATION_ENERGY_MAX gauge points)
// with a CHECKPOINT tick at the next rotation spell's cost: when the fill
// reaches the tick, that spell fires. The fill never resets to zero — the
// surplus past the checkpoint stays banked (tempo builds keep their edge).
export function RotationGauge({ energy, max, checkpoint, checkpointIcon, enemy = false }: {
  energy: number; max: number; checkpoint: number | null; checkpointIcon?: string; enemy?: boolean;
}) {
  const pct = Math.max(0, Math.min(100, (energy / max) * 100));
  const cpPct = checkpoint != null ? Math.max(0, Math.min(100, (checkpoint / max) * 100)) : null;
  const reached = checkpoint != null && energy >= checkpoint;
  return (
    <div style={{ position: "relative", flex: 1, height: 16, borderRadius: 8, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)" }}>
      <div style={{ position: "absolute", inset: 0, borderRadius: 8, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: enemy ? "linear-gradient(90deg,#FF8A90,#FF3B47)" : "linear-gradient(90deg,#4CC9F0,#7FE0FF)", boxShadow: enemy ? "0 0 10px rgba(255,59,71,0.5)" : "0 0 10px rgba(76,201,240,0.55)", transition: "width 0.35s ease" }} />
      </div>
      {cpPct != null && (
        <div style={{ position: "absolute", left: `${cpPct}%`, top: -4, bottom: -4, width: 2, background: reached ? "#2DE36B" : "#F5B301", boxShadow: `0 0 6px ${reached ? "rgba(45,227,107,0.8)" : "rgba(245,179,1,0.8)"}`, transform: "translateX(-1px)" }}>
          {checkpointIcon && (
            <span style={{ position: "absolute", top: -17, left: "50%", transform: "translateX(-50%)", fontSize: 11, lineHeight: 1 }}>{checkpointIcon}</span>
          )}
        </div>
      )}
    </div>
  );
}

// LIVE fight driven by the player: the pveCombatInteractive state machine holds
// ALL the rules (energy, one auto per turn, spell costs, enemy reply); this
// component only dispatches actions and renders the state. Nothing is persisted
// here — the parent commits once via handleLiveCombatFinished.
function InteractiveCombatScreen({ stage, initial, opponentImg, opponentName, homeLayers, petImg, enemyPetImg, onFinished }: {
  stage: number;
  initial: InteractiveCombatState;
  opponentImg: string;
  opponentName: string;
  homeLayers?: AvatarLayers;
  /** 2v2 pets test — equipped familiar artwork (null = emoji fallback). */
  petImg?: string | null;
  /** 2v2 pets test — the enemy's companion sprite (randomized per room). */
  enemyPetImg?: string | null;
  onFinished: (finalState: InteractiveCombatState) => void;
}) {
  const { st, lang } = useST();
  const [combat, setCombat] = useState<InteractiveCombatState>(initial);
  const [showSpells, setShowSpells] = useState(false); // "view skills" recap popup
  const [enemyActing, setEnemyActing] = useState(false); // brief lock while the enemy reply plays
  // Per-ACTOR frame FX: who acted (lunge) and who got hit (shake) — a master's
  // attack never moves its pet, and vice versa.
  type CombatUnit = "player" | "boss" | "allyPet" | "enemyPet";
  const [actorFx, setActorFx] = useState<CombatUnit | null>(null);
  const [hitFx, setHitFx] = useState<CombatUnit | null>(null);
  const finishedRef = useRef(false);
  const endTurnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Rotation cast UX — transient gauge snapshot shown BEFORE the consumption
  // animation: the fill at the checkpoint of the spell that just fired (the
  // state already points to the NEXT spell, which would tell the wrong story).
  const [gaugePeak, setGaugePeak] = useState<{ value: number; checkpoint: number; icon?: string } | null>(null);
  const gaugeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const framesLen = combat.frames.length;
  const last = framesLen > 0 ? combat.frames[framesLen - 1] : null;

  // Hit-react / lunge flash after each new frame (cleared shortly after).
  useEffect(() => {
    if (!last) return;
    setActorFx((last.actor as CombatUnit | undefined) ?? last.attacker);
    setHitFx(last.damage > 0
      ? (last.attacker === "player" ? (last.targetPet ? "enemyPet" : "boss") : (last.targetPet ? "allyPet" : "player"))
      : null);
    const t = setTimeout(() => { setActorFx(null); setHitFx(null); }, 420);
    return () => clearTimeout(t);
  }, [framesLen]); // eslint-disable-line

  // Fight over → short hold on the final state, then hand off (once).
  useEffect(() => {
    if (!combat.over || finishedRef.current) return;
    const t = setTimeout(() => {
      if (!finishedRef.current) { finishedRef.current = true; onFinished(combat); }
    }, 900);
    return () => clearTimeout(t);
  }, [combat]); // eslint-disable-line

  // Runs the enemy reply from an EXPLICIT base state — the rotation driver
  // hands the freshly-acted state directly (the `combat` closure would be stale).
  const doEndTurnFrom = (base: InteractiveCombatState) => {
    setEnemyActing(true);
    // Small beat so the enemy reply reads as ITS turn, not an instant riposte.
    endTurnTimerRef.current = setTimeout(() => {
      const next = endTurn(base);
      const startLen = base.frames.length;
      const added = next.frames.length - startLen;
      // 0-1 new frame → nothing to stagger; apply the final state directly.
      if (added <= 1) { setCombat(next); setEnemyActing(false); return; }
      // Reveal the enemy's actions one by one. Every frame is a full HP snapshot,
      // so a partial state (final `next` + that frame's HP/shield) animates each
      // hit on its own; the last step commits the real `next` (over/win flags).
      const revealStep = (k: number) => {
        if (k >= added - 1) { setCombat(next); setEnemyActing(false); return; }
        const f = next.frames[startLen + k];
        setCombat({
          ...next,
          over: false, win: null,
          frames: next.frames.slice(0, startLen + k + 1),
          playerHp: f.playerHp,
          enemyHp: f.bossHp,
          playerShield: f.playerShield,
          petHp: f.petHp ?? next.petHp,
          enemyPetHp: f.enemyPetHp ?? next.enemyPetHp,
        });
        endTurnTimerRef.current = setTimeout(() => revealStep(k + 1), ENEMY_STEP_MS);
      };
      revealStep(0);
    }, 520);
  };

  const doEndTurn = () => {
    if (enemyActing || combat.over) return;
    doEndTurnFrom(combat);
  };

  // Auto-validate: "valider ou finir mes énergies" — the turn self-ends only at
  // 0 energy with nothing left to do (leftover energy = deliberate banking).
  // (Always false in rotation mode — see shouldAutoEndTurn.)
  useEffect(() => {
    if (enemyActing || combat.over || !shouldAutoEndTurn(combat)) return;
    const t = setTimeout(doEndTurn, 650);
    return () => clearTimeout(t);
  }, [combat, enemyActing]); // eslint-disable-line

  // ── Rotation mode (Ulala test): the fight plays itself ──
  // Each cycle: auto attack (+10 gauge) → rotation head if affordable → enemy
  // reply. setEnemyActing(true) locks this effect out until the reply resolves.
  // Cast UX: the gauge FILLS to the checkpoint first (gaugePeak), holds a beat,
  // THEN plays the mana consumption — without this the bar would jump straight
  // to the post-cast value and never visually reach the spell's cost.
  useEffect(() => {
    if (!combat.rotationMode || enemyActing || combat.over) return;
    const t = setTimeout(() => {
      setEnemyActing(true);
      const head = rotationHead(combat);
      const acted = rotationPlayerAction(combat);
      const casted = acted.rotationIndex > combat.rotationIndex;
      if (casted && head) {
        setGaugePeak({
          value: Math.min(ROTATION_ENERGY_MAX, combat.energy + combat.energyPerAuto),
          checkpoint: rotationSpellCost(head),
          icon: getSpell(head)?.icon,
        });
        gaugeTimerRef.current = setTimeout(() => setGaugePeak(null), 550);
      }
      setCombat(acted);
      if (acted.over) { setEnemyActing(false); return; }
      // Let the player action read before the reply (longer when the fill →
      // drain animation plays).
      endTurnTimerRef.current = setTimeout(() => doEndTurnFrom(acted), casted ? 1250 : 700);
    }, 750);
    return () => clearTimeout(t);
  }, [combat, enemyActing]); // eslint-disable-line

  useEffect(() => () => {
    if (endTurnTimerRef.current) clearTimeout(endTurnTimerRef.current);
    if (gaugeTimerRef.current) clearTimeout(gaugeTimerRef.current);
  }, []);

  const doSpell = (id: PveSpellId) => { if (!enemyActing && !combat.over) setCombat((c) => castSpell(c, id)); };
  const doPetSpell = (id: PetSpellId) => { if (!enemyActing && !combat.over) setCombat((c) => castPetSpell(c, id)); };

  // Floating cue for the last player action (spell name / crit / heal / refund).
  const lastIsPlayer = last?.attacker === "player";
  // ── 2v2 pets test ──
  const pets = combat.petsEnabled;
  const petPhase = pets && combat.phase === "pet";
  const petDead = pets && combat.petHp <= 0;
  const enemyPetDead = pets && combat.enemyPetHp <= 0;

  return (
    // Fullscreen combat: a fixed overlay covers the whole app (nav bar included)
    // so the fight breathes; spells + End Turn stick to the bottom edge.
    <div style={{ position: "fixed", inset: 0, zIndex: 800, overflowY: "auto", background: "linear-gradient(180deg,#0b1322,#070d18)", fontFamily: FONT.ui }}>
    <div style={{ minHeight: "100%", maxWidth: 480, margin: "0 auto", boxSizing: "border-box", padding: "14px 16px calc(14px + env(safe-area-inset-bottom, 0px))", display: "flex", flexDirection: "column", gap: 14 }}>
      <style>{ST_STYLE}</style>
      <style>{ST_COMBAT_KEYFRAMES}</style>

      {/* Header — "S1 · 3" left, turn-order bar dead center (absolute, immune
          to the side elements' widths), spells recap right. */}
      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minHeight: 46 }}>
        <span style={{ font: `700 15px ${FONT.display}`, letterSpacing: 1.5, textTransform: "uppercase", color: C.text, whiteSpace: "nowrap" }}>
          S{stage} · {combat.round}
        </span>
        {pets && (
          <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}>
            <TurnOrderBar
              active={enemyActing ? (combat.phase === "player" ? 1 : 3) : petPhase ? 2 : 0}
              slots={[
                { icon: "👤", ally: true, hp: combat.playerHp, maxHp: combat.playerMaxHp },
                { icon: "👤", ally: false, hp: combat.enemyHp, maxHp: combat.enemyMaxHp },
                { icon: "🐕", ally: true, hp: combat.petHp, maxHp: combat.petMaxHp },
                { icon: "🐕", ally: false, hp: combat.enemyPetHp, maxHp: combat.enemyPetMaxHp },
              ]}
            />
          </div>
        )}
        <button onClick={() => setShowSpells(true)} style={{ flex: "none", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.05)", color: C.text, borderRadius: 8, padding: "5px 9px", font: `700 10px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer", whiteSpace: "nowrap" }}>
          {st("viewSpells")}
        </button>
      </div>

      {/* Arena — same two FighterColumns as the control CombatScreen */}
      <div style={{ position: "relative", overflow: "hidden", borderRadius: 16, padding: "14px 10px 12px", background: "radial-gradient(300px 200px at 50% 120%, rgba(45,227,107,0.10), transparent 70%), linear-gradient(180deg,#0c1626,#070d18)", border: "1px solid rgba(255,255,255,0.07)" }}>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "repeating-linear-gradient(0deg, rgba(76,201,240,0.05) 0 1px, transparent 1px 26px)" }} />
        <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "flex-end", gap: 8 }}>
          <FighterColumn
            hp={Math.round(combat.playerHp)}
            maxHp={combat.playerMaxHp}
            shield={Math.round(combat.playerShield)}
            tone="player"
            name={String(st("you"))}
            shaking={false}
            attacking={false}
            lungeDir="right"
            shakeIdx={framesLen}
            dead={pets ? combat.playerHp <= 0 && petDead : combat.playerHp <= 0}
            petBar={pets ? { hp: Math.round(combat.petHp), maxHp: combat.petMaxHp } : undefined}
          >
            <FighterStage
              side="ally"
              charScale={PLAYER_SCALE}
              dimChar={petPhase}
              deadChar={pets && combat.playerHp <= 0}
              charShaking={hitFx === "player"}
              charAttacking={actorFx === "player"}
              petShaking={hitFx === "allyPet"}
              petAttacking={actorFx === "allyPet"}
              shakeIdx={framesLen}
              pet={pets ? { img: petImg ?? null, hp: combat.petHp, maxHp: combat.petMaxHp, dead: petDead, active: petPhase } : null}
            >
              {/* The familiar layer moves OFF the master (it stands beside as the pet). */}
              <LayeredCharacter layers={pets ? { ...homeLayers, familiar: null } : homeLayers} glowColor={actorFx === "player" && hitFx ? "rgba(45,227,107,0.9)" : undefined} style={{ height: "100%", width: "auto" }} />
            </FighterStage>
          </FighterColumn>

          {/* Central zone — VS marker + floating cues for the LAST frame */}
          <div style={{ width: ARENA_VS_W, flexShrink: 0, alignSelf: "center", position: "relative", display: "flex", justifyContent: "center" }}>
            <div style={{ font: `700 13px ${FONT.display}`, letterSpacing: 1, color: "#4CC9F0", textShadow: "0 0 10px rgba(76,201,240,0.6)" }}>VS</div>
            {/* Projectile — flies from attacker toward the defender */}
            {last && last.damage > 0 && (
              <div
                key={`proj-${framesLen}`}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: -10,
                  marginLeft: -10,
                  width: 20,
                  height: 20,
                  fontSize: 16,
                  lineHeight: "20px",
                  textAlign: "center",
                  animation: lastIsPlayer
                    ? "stProjFlyR 0.22s ease-out both"
                    : "stProjFlyL 0.22s ease-out both",
                  pointerEvents: "none",
                  zIndex: 10,
                }}
              >
                ⚽
              </div>
            )}
            {last && last.damage > 0 && (
              <div
                key={`dmg-${framesLen}`}
                style={{
                  position: "absolute", top: -26,
                  ...(lastIsPlayer ? { left: "50%", marginLeft: DMG_OFFSET } : { right: "50%", marginRight: DMG_OFFSET }),
                  font: `700 ${last.crit ? 22 : 18}px ${FONT.display}`,
                  color: last.crit ? C.gold : C.live,
                  animation: "stDmgPop 0.85s ease-out both",
                  pointerEvents: "none", whiteSpace: "nowrap", textAlign: "center",
                }}
              >
                {last.crit && <div style={{ font: `700 10px ${FONT.label}`, letterSpacing: 1, color: C.gold }}>{st("crit")}</div>}
                -{last.damage}
                {/* Energy refund cue — spells carrying critEnergyRefund (Nutmeg). */}
                {lastIsPlayer && last.crit && last.spell && (getSpell(last.spell)?.effects.critEnergyRefund ?? 0) > 0 && (
                  <div style={{ font: `700 10px ${FONT.label}`, letterSpacing: 1, color: C.gold }}>{st("critRefund")}</div>
                )}
              </div>
            )}
            {last && last.spell && last.attacker === "player" ? (
              <div key={`sp-${framesLen}`} style={{ position: "absolute", top: -52, left: "50%", marginLeft: DMG_OFFSET, fontSize: 12, fontWeight: 900, color: spellColor(last.spell), animation: "stDmgPop 0.85s ease-out both", pointerEvents: "none", whiteSpace: "nowrap" }}>
                {spellIcon(last.spell)} {spellName(last.spell, lang)}
              </div>
            ) : null}
            {/* Heals — player heals on the player's side, mob heals on the enemy's */}
            {last && last.heal && last.attacker === "player" ? (
              <div key={`heal-${framesLen}`} style={{ position: "absolute", top: -26, right: "50%", marginRight: DMG_OFFSET, fontSize: 16, fontWeight: 900, color: C.turf, animation: "stDmgPop 0.85s ease-out both", pointerEvents: "none", whiteSpace: "nowrap" }}>
                +{last.heal}💚
              </div>
            ) : null}
            {last && last.heal && last.attacker === "boss" ? (
              <div key={`eheal-${framesLen}`} style={{ position: "absolute", top: -26, left: "50%", marginLeft: DMG_OFFSET, fontSize: 16, fontWeight: 900, color: C.turf, animation: "stDmgPop 0.85s ease-out both", pointerEvents: "none", whiteSpace: "nowrap" }}>
                +{last.heal}💚
              </div>
            ) : null}
            {/* Reckless Strike — HP paid by the caster, shown on the player's side */}
            {last && last.selfDamage ? (
              <div key={`self-${framesLen}`} style={{ position: "absolute", top: -26, right: "50%", marginRight: DMG_OFFSET, fontSize: 15, fontWeight: 900, color: C.live, animation: "stDmgPop 0.85s ease-out both", pointerEvents: "none", whiteSpace: "nowrap" }}>
                -{last.selfDamage}🩸
              </div>
            ) : null}
          </div>

          <FighterColumn
            hp={Math.round(combat.enemyHp)}
            maxHp={combat.enemyMaxHp}
            tone="enemy"
            name={opponentName}
            shaking={false}
            attacking={false}
            lungeDir="left"
            shakeIdx={framesLen}
            dead={pets ? combat.enemyHp <= 0 && enemyPetDead : combat.enemyHp <= 0}
            petBar={pets ? { hp: Math.round(combat.enemyPetHp), maxHp: combat.enemyPetMaxHp } : undefined}
          >
            <FighterStage
              side="enemy"
              deadChar={pets && combat.enemyHp <= 0}
              charShaking={hitFx === "boss"}
              charAttacking={actorFx === "boss"}
              petShaking={hitFx === "enemyPet"}
              petAttacking={actorFx === "enemyPet"}
              shakeIdx={framesLen}
              petLayer
              pet={pets ? { img: enemyPetImg ?? null, hp: combat.enemyPetHp, maxHp: combat.enemyPetMaxHp, dead: enemyPetDead } : null}
            >
              <img
                src={opponentImg}
                alt="Opponent"
                style={{ display: "block", height: "100%", width: "auto", objectFit: "contain", filter: actorFx === "boss" && hitFx ? "drop-shadow(0 0 10px rgba(255,59,71,0.9))" : undefined, transition: "filter .18s ease-out" }}
              />
            </FighterStage>
          </FighterColumn>
        </div>
      </div>

      {/* Energy rows — the player's gauge AND the enemy's VISIBLE gauge (its 5⚡
          spell is telegraphed: warning shown when its next turn can afford it). */}
      <div style={{ display: "flex", flexDirection: "column", gap: 9, background: "linear-gradient(180deg,#121a2b,#0c1322)", borderRadius: 14, padding: "11px 14px", border: "1px solid rgba(255,255,255,0.08)", clipPath: ST_BEVEL }}>
        {/* Ally gauge — switches with the ACTIVE character: the player's mana,
            or the pet's 4-pip bar during the pet phase. */}
        {combat.rotationMode ? (
          // Rotation gauge — fill + checkpoint (next rotation spell's cost) +
          // income per auto. The number after a cast IS the kept surplus.
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ font: `800 11px ${FONT.label}`, letterSpacing: 2, textTransform: "uppercase", color: C.muted, display: "flex", alignItems: "center", gap: 7, flex: "none" }}>
                <span style={{ fontSize: 14 }}>💧</span> {st("energyLabel")}
              </span>
              <RotationGauge
                energy={gaugePeak ? gaugePeak.value : combat.energy}
                max={ROTATION_ENERGY_MAX}
                checkpoint={gaugePeak ? gaugePeak.checkpoint : rotationHead(combat) ? rotationSpellCost(rotationHead(combat)!) : null}
                checkpointIcon={gaugePeak ? gaugePeak.icon : rotationHead(combat) ? getSpell(rotationHead(combat)!)?.icon : undefined}
              />
              <span style={{ font: `700 15px ${FONT.display}`, color: "#4CC9F0", minWidth: 52, textAlign: "right", flex: "none" }}>
                {gaugePeak ? gaugePeak.value : combat.energy}/{ROTATION_ENERGY_MAX}
              </span>
            </div>
          </>
        ) : (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ font: `800 11px ${FONT.label}`, letterSpacing: 2, textTransform: "uppercase", color: C.muted, display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: 14 }}>{petPhase ? "🐾" : "💧"}</span> {petPhase ? st("petLabel") : st("energyLabel")}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <EnergyPips energy={petPhase ? combat.petEnergy : combat.energy} max={petPhase ? PET_ENERGY_PER_TURN : combat.energyPerTurn} />
            <span style={{ font: `700 15px ${FONT.display}`, color: "#4CC9F0", minWidth: 34, textAlign: "right" }}>
              {petPhase ? `${combat.petEnergy}/${PET_ENERGY_PER_TURN}` : `${combat.energy}/${combat.energyPerTurn}`}
            </span>
          </div>
        </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ font: `800 11px ${FONT.label}`, letterSpacing: 2, textTransform: "uppercase", color: "#FF6B6B", display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: 14 }}>👹</span> {st("enemyEnergyLabel")}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <EnergyPips energy={combat.enemyEnergy} max={combat.enemyEnergyMax} enemy />
            <span style={{ font: `700 13px ${FONT.display}`, color: "#FF6B6B", minWidth: 34, textAlign: "right" }}>{combat.enemyEnergy}/{combat.enemyEnergyMax}</span>
          </div>
        </div>
      </div>

      {/* Active buffs — Focus / Off Balance / Battle Cry / enemy rage passive */}
      {(combat.focusBonusPercent > 0 || combat.offBalancePercent > 0 || combat.masterBoostPercent > 0 || enemyRageMultiplier(combat) > 1) && (
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
          {combat.masterBoostPercent > 0 && (
            <span style={{ font: `700 10px ${FONT.label}`, letterSpacing: 0.6, color: "#F5B301", background: "rgba(245,179,1,0.13)", border: "1px solid rgba(245,179,1,0.4)", borderRadius: 7, padding: "4px 9px" }}>
              {st("buffBattleCry", combat.masterBoostPercent)}
            </span>
          )}
          {combat.focusBonusPercent > 0 && (
            <span style={{ font: `700 10px ${FONT.label}`, letterSpacing: 0.6, color: "#C084FC", background: "rgba(192,132,252,0.13)", border: "1px solid rgba(192,132,252,0.4)", borderRadius: 7, padding: "4px 9px" }}>
              {st("buffFocus", combat.focusBonusPercent)}
            </span>
          )}
          {combat.offBalancePercent > 0 && (
            <span style={{ font: `700 10px ${FONT.label}`, letterSpacing: 0.6, color: C.gold, background: "rgba(245,179,1,0.13)", border: "1px solid rgba(245,179,1,0.4)", borderRadius: 7, padding: "4px 9px" }}>
              {st("buffOffBalance", combat.offBalancePercent)}
            </span>
          )}
          {enemyRageMultiplier(combat) > 1 && (
            <span style={{ font: `700 10px ${FONT.label}`, letterSpacing: 0.6, color: "#FF6B6B", background: "rgba(255,59,71,0.13)", border: "1px solid rgba(255,59,71,0.4)", borderRadius: 7, padding: "4px 9px" }}>
              {st("buffEnemyRage", Math.round((enemyRageMultiplier(combat) - 1) * 100))}
            </span>
          )}
        </div>
      )}

      {/* Bottom block — spells + End Turn stick to the screen's bottom edge
          (marginTop auto) so the arena breathes. */}
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 12, paddingTop: 8 }}>
      {/* Spell bar — the ACTIVE ally's kit: the pet's 3 spells during the pet
          phase, otherwise the run's locked spells (cost DESC left→right),
          disabled when unaffordable / on cooldown / enemy acting */}
      {petPhase ? (
        <div style={{ display: "flex", gap: 8 }}>
          {PET_SPELL_POOL.map((id) => {
            const def = getPetSpell(id)!;
            const cd = combat.petCooldowns[id] ?? 0;
            const usable = !enemyActing && canCastPetSpell(combat, id);
            const col = def.color;
            // Concrete numbers for this master (display only — engine recomputes).
            const hint = id === "pet_strike"
              ? st("hintSpellDmg", Math.round((combat.playerStats.physique * PET_STRIKE_DAMAGE_PERCENT) / 100))
              : id === "pet_zone_heal"
                ? `+${Math.round((combat.playerStats.defense * PET_HEAL_PERCENT_MASTER_DEF) / 100)} HP ×2`
                : null;
            return (
              <button
                key={id}
                onClick={() => doPetSpell(id)}
                disabled={!usable}
                style={{
                  flex: 1, minWidth: 0, position: "relative",
                  borderRadius: 12, padding: "10px 4px 8px",
                  background: usable ? `linear-gradient(180deg,${col}20,${col}08)` : "rgba(255,255,255,0.03)",
                  border: `1.5px solid ${usable ? col + "88" : "rgba(255,255,255,0.08)"}`,
                  boxShadow: usable ? `0 0 14px ${col}33` : "none",
                  opacity: usable ? 1 : 0.45,
                  cursor: usable ? "pointer" : "not-allowed",
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                  transition: "opacity 0.15s, box-shadow 0.15s",
                }}
              >
                <span style={{ fontSize: 21, lineHeight: 1 }}>{def.icon}</span>
                <span style={{ font: `700 9px ${FONT.label}`, letterSpacing: 0.4, color: usable ? col : C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{spellName(id, lang)}</span>
                <span style={{ font: `800 10px ${FONT.display}`, color: "#fff" }}>{st("spellCost", def.cost)}</span>
                {hint && <span style={{ font: `700 9px ${FONT.display}`, color: usable ? "#9fb0c8" : C.muted }}>{hint}</span>}
                {cd > 0 && (
                  <span style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(7,13,24,0.62)", borderRadius: 12, font: `800 13px ${FONT.display}`, color: C.text }}>
                    {st("cooldownShort", cd)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
      <div style={{ display: "flex", gap: 8 }}>
        {/* Rotation mode: the bar is a READ-ONLY timeline in ROTATION order —
            the head glows, numbers show the firing order. Manual mode keeps
            the cost-sorted clickable buttons. */}
        {(combat.rotationMode ? combat.loadout : sortByCostDesc(combat.loadout)).map((id, slotIdx) => {
          const def = getSpell(id);
          if (!def) return null;
          const cd = combat.cooldowns[id] ?? 0;
          const usedOnce = !!(def.effects.oncePerTurn && combat.oncePerTurnCast[id]);
          const isHead = combat.rotationMode && rotationHead(combat) === id && slotIdx === combat.rotationIndex % combat.loadout.length;
          const usable = combat.rotationMode ? isHead : (!enemyActing && canCastSpell(combat, id));
          const col = def.color;
          return (
            <button
              key={`${id}-${slotIdx}`}
              onClick={() => { if (!combat.rotationMode) doSpell(id); }}
              disabled={combat.rotationMode || !usable}
              style={{
                flex: 1, minWidth: 0, position: "relative",
                borderRadius: 12, padding: "10px 4px 8px",
                background: usable ? `linear-gradient(180deg,${col}20,${col}08)` : "rgba(255,255,255,0.03)",
                border: `1.5px solid ${usable ? col + "88" : "rgba(255,255,255,0.08)"}`,
                boxShadow: usable ? `0 0 14px ${col}33` : "none",
                opacity: usable ? 1 : 0.45,
                cursor: combat.rotationMode ? "default" : usable ? "pointer" : "not-allowed",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                transition: "opacity 0.15s, box-shadow 0.15s",
              }}
            >
              {combat.rotationMode && (
                <span style={{ position: "absolute", top: 4, left: 6, font: `800 10px ${FONT.display}`, color: isHead ? col : C.muted }}>{slotIdx + 1}</span>
              )}
              <span style={{ fontSize: 21, lineHeight: 1 }}>{def.icon}</span>
              <span style={{ font: `700 9px ${FONT.label}`, letterSpacing: 0.4, color: usable ? col : C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{spellName(id, lang)}</span>
              <span style={{ font: `800 10px ${FONT.display}`, color: "#fff" }}>{st("spellCost", combat.rotationMode ? rotationSpellCost(id) : def.cost)}</span>
              {(() => {
                // Primary concrete value for this player (damage, or shield for
                // Wall). Rotation: damage spells hit ×ROTATION_SPELL_DAMAGE_MULT.
                const v = spellComputedValues(id, combat.playerStats.physique, combat.playerMaxHp, combat.playerLevel);
                const dmg = v.damage != null ? Math.round(v.damage * (combat.rotationMode ? ROTATION_SPELL_DAMAGE_MULT : 1)) : null;
                const label = dmg != null ? st("hintSpellDmg", dmg) : v.shield != null ? st("hintSpellShield", v.shield) : null;
                return label ? <span style={{ font: `700 9px ${FONT.display}`, color: usable ? "#9fb0c8" : C.muted }}>{label}</span> : null;
              })()}
              {(cd > 0 || usedOnce) && (
                <span style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(7,13,24,0.62)", borderRadius: 12, font: `800 ${cd > 0 ? 13 : 10}px ${FONT.display}`, color: C.text, letterSpacing: cd > 0 ? 0 : 1, textTransform: "uppercase" }}>
                  {cd > 0 ? st("cooldownShort", cd) : st("oncePerTurnUsed")}
                </span>
              )}
            </button>
          );
        })}
      </div>
      )}

      {/* Action row — End Turn only (spells are the only actions; the turn
          auto-ends when nothing is castable, since leftovers don't carry).
          Hidden in rotation mode: the fight plays itself. */}
      {!combat.rotationMode && (
      <button
        onClick={doEndTurn}
        disabled={enemyActing || combat.over}
        style={{
          background: enemyActing || combat.over ? "rgba(255,255,255,0.06)" : "linear-gradient(135deg,#2DE36B,#159B52)",
          border: "none", borderRadius: 14, padding: "14px 0",
          font: `800 15px ${FONT.display}`, letterSpacing: 1.5, textTransform: "uppercase",
          color: enemyActing || combat.over ? C.muted : "#fff",
          cursor: enemyActing || combat.over ? "not-allowed" : "pointer",
          clipPath: ST_BEVEL,
        }}
      >
        {st("endTurn")}
      </button>
      )}
      </div>

      {/* "View skills" recap — the run's locked spells with effects + live numbers */}
      {showSpells && (
        <div onClick={() => setShowSpells(false)} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 480, maxHeight: "80vh", overflowY: "auto", background: "linear-gradient(180deg,#141d31,#0b1120)", borderTopLeftRadius: 22, borderTopRightRadius: 22, borderTop: "1px solid rgba(255,255,255,0.1)", padding: "18px 16px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ font: `700 18px ${FONT.display}`, letterSpacing: 1, textTransform: "uppercase", color: C.text, textAlign: "center" }}>{st("yourSpells")}</div>
            {(combat.rotationMode ? combat.loadout : sortByCostDesc(combat.loadout)).map((id) => {
              const def = getSpell(id);
              if (!def) return null;
              const cd = combat.cooldowns[id] ?? 0;
              const chips = spellValueChips(id, combat.playerStats.physique, combat.playerMaxHp, combat.playerLevel, st);
              return (
                <div key={id} style={{ display: "flex", gap: 11, alignItems: "flex-start", background: `linear-gradient(180deg,${def.color}12,${def.color}04)`, border: `1px solid ${def.color}44`, borderRadius: 13, padding: "11px 12px" }}>
                  <span style={{ flex: "none", width: 42, height: 42, display: "grid", placeItems: "center", borderRadius: 10, fontSize: 22, background: `${def.color}22`, border: `1px solid ${def.color}55` }}>{def.icon}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ font: `700 14px ${FONT.display}`, color: def.color }}>{spellName(id, lang)}</span>
                      <span style={{ font: `800 11px ${FONT.display}`, color: "#fff" }}>{st("spellCost", combat.rotationMode ? rotationSpellCost(id) : def.cost)}</span>
                      {cd > 0 && <span style={{ font: `800 10px ${FONT.label}`, color: C.muted }}>{st("cooldownShort", cd)}</span>}
                    </div>
                    <div style={{ font: `500 12px ${FONT.ui}`, color: C.muted, lineHeight: 1.35, marginTop: 3 }}>{spellDesc(id, lang)}</div>
                    {chips.length > 0 && (
                      <div style={{ font: `800 11px ${FONT.display}`, color: C.gold, marginTop: 4 }}>{chips.join(" · ")}</div>
                    )}
                  </div>
                </div>
              );
            })}
            {pets && (
              <>
                <div style={{ font: `700 14px ${FONT.display}`, letterSpacing: 1, textTransform: "uppercase", color: "#4CC9F0", textAlign: "center", marginTop: 8 }}>🐾 {st("companionSpells")}</div>
                {PET_SPELL_POOL.map((id) => {
                  const def = getPetSpell(id)!;
                  const cd = combat.petCooldowns[id] ?? 0;
                  return (
                    <div key={id} style={{ display: "flex", gap: 11, alignItems: "flex-start", background: `linear-gradient(180deg,${def.color}12,${def.color}04)`, border: `1px solid ${def.color}44`, borderRadius: 13, padding: "11px 12px" }}>
                      <span style={{ flex: "none", width: 42, height: 42, display: "grid", placeItems: "center", borderRadius: 10, fontSize: 22, background: `${def.color}22`, border: `1px solid ${def.color}55` }}>{def.icon}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ font: `700 14px ${FONT.display}`, color: def.color }}>{spellName(id, lang)}</span>
                          <span style={{ font: `800 11px ${FONT.display}`, color: "#fff" }}>{st("spellCost", def.cost)}</span>
                          {cd > 0 && <span style={{ font: `800 10px ${FONT.label}`, color: C.muted }}>{st("cooldownShort", cd)}</span>}
                        </div>
                        <div style={{ font: `500 12px ${FONT.ui}`, color: C.muted, lineHeight: 1.35, marginTop: 3 }}>{spellDesc(id, lang)}</div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
            <button onClick={() => setShowSpells(false)} style={{ marginTop: 4, width: "100%", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.05)", color: C.text, borderRadius: 10, padding: "11px 0", font: `700 12px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer" }}>{st("close")}</button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
